import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, Users, FolderOpen, MessageSquare, Settings as SettingsIcon, Search, LogOut, Menu, X, Calendar, FileText, Upload, ExternalLink, Mail, Lock, Eye, EyeOff, Bell, CheckCircle, CheckSquare, User, Activity, Building2, UserCheck, ChevronDown, ChevronRight, Clock, Play, Square, BookOpen, UserPlus, Zap, AlertTriangle, RefreshCw, Gavel } from 'lucide-react';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import api, { API_BASE_URL } from './utils/api';

const API_V1_BASE = process.env.REACT_APP_API_V1_BASE_URL || 'https://api.projectathena.us';
import Settings from './components/Settings';
import TaskHub from './components/TaskHub';
import ScheduleDashboard from './components/ScheduleDashboard';
import OrganizationDashboard from './components/OrganizationDashboard';
import ProjectDetail from './components/ProjectDetail';
import NotificationCenter from './components/NotificationCenter';
import EnhancedClientForm from './components/EnhancedClientForm';
import SuperAdminApp from './components/SuperAdminApp';
import UserProfile from './components/UserProfile';
import ClientActivityFeed from './components/ClientActivityFeed';
import Governance from './components/Governance';
import DocumentViewer from './components/DocumentViewer';
import LiveMeetingRunner from './components/LiveMeetingRunner';
import BoardSettings from './components/BoardSettings';
import CommitteeDashboard from './components/CommitteeDashboard';
import CommitteeSettings from './components/CommitteeSettings';
import CommitteeMeetingRunner from './components/CommitteeMeetingRunner';
import PasswordReset from './components/PasswordReset';
import NewPortalLogin from './components/NewPortalLogin';
import PortalEmailVerification from './components/PortalEmailVerification';
import PortalPasswordSetup from './components/PortalPasswordSetup';
import PortalForgotPassword from './components/PortalForgotPassword';
import PortalResetPassword from './components/PortalResetPassword';
import PortalResendVerification from './components/PortalResendVerification';
import PortalSelectClient from './components/PortalSelectClient';
import NewPortalDashboard from './components/NewPortalDashboard_v2';
import LegacyPortalRedirect from './components/LegacyPortalRedirect';
import Signup from './components/Signup';
import AcceptInvitation from './components/AcceptInvitation';
import PricingSignup from './components/PricingSignup';
import SignupSuccess from './components/SignupSuccess';
import ReactivateAccount from './components/ReactivateAccount';
import GracePeriodBanner from './components/GracePeriodBanner';
import TestComponent from './components/TestComponent';
import { TaskModal } from './components/TaskManagement';
import NotesTab from './components/NotesTab';
import QuickAddTaskModal from './components/QuickAddTaskModal';
import AttachmentPreview from './components/AttachmentPreview';
import ClientSettings from './components/ClientSettings';
import ImportWizard from './components/ImportWizard';
import OrganizationHeader from './components/OrganizationHeader';
import UnifiedInbox from './components/UnifiedInbox';
import GlobalChatWidget from './components/chat/GlobalChatWidget';
import { ChatContextProvider, useChatContext } from './context/ChatContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Footer from './components/Footer';
import TermsOfUse from './components/TermsOfUse';
import PrivacyPolicy from './components/PrivacyPolicy';
import Support from './components/Support';
import ZoomDocumentation from './components/ZoomDocumentation';
import Badge from './components/Badge';
import KeyboardShortcutsHelp from './components/KeyboardShortcutsHelp';
import { useKeyboardShortcuts, getShortcuts, initializeShortcuts } from './hooks/useKeyboardShortcuts';
import MFAVerify from './components/MFAVerify';

// Utility function to generate user initials
const getUserInitials = (name) => {
  if (!name || typeof name !== 'string') return 'U';
  
  const nameParts = name.trim().split(' ').filter(part => part.length > 0);
  if (nameParts.length === 0) return 'U';
  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }
  
  // Return first letter of first name + first letter of last name
  return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
};

// Priority configuration for tasks
const PRIORITIES = {
  'Low': { color: 'text-green-600', bgColor: 'bg-green-100', icon: 'ß╡ó' },
  'Normal': { color: 'text-blue-600', bgColor: 'bg-blue-100', icon: 'ΓùÅ' },
  'High': { color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: 'Γû▓' },
  'Critical': { color: 'text-red-600', bgColor: 'bg-red-100', icon: 'ΓÜá' }
};

// Status configuration  
const STATUSES = {
  'To Do': { color: 'text-gray-600', bgColor: 'bg-gray-100' },
  'In Progress': { color: 'text-blue-600', bgColor: 'bg-blue-100' },
  'Blocked': { color: 'text-red-600', bgColor: 'bg-red-100' },
  'Done': { color: 'text-green-600', bgColor: 'bg-green-100' }
};

// Custom hooks and utilities
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for OAuth callback parameters
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get('access_token');
    const oauthToken = urlParams.get('token'); // New format from backend
    const googleToken = urlParams.get('google_token'); // Google Sign-In token
    const success = urlParams.get('success'); // New format from backend
    const userName = urlParams.get('user_name');
    const userEmail = urlParams.get('user_email');
    const integrationAdded = urlParams.get('integration_added');
    const error = urlParams.get('error');
    const errorMessage = urlParams.get('message');
    
    // IMPORTANT: Save task_id parameter before OAuth cleanup clears it
    const taskId = urlParams.get('task_id');
    if (taskId) {
      console.log('≡ƒôº Found task_id from email link, saving before OAuth cleanup:', taskId);
      sessionStorage.setItem('pending_task_id', taskId);
    }
    
    // Handle OAuth errors first - prevent any API calls during error processing
    if (error) {
      const message = urlParams.get('message');
      const userContext = urlParams.get('user_context');
      
      // Show user-friendly error message
      if (error === 'token_exchange_failed') {
        alert(`GoHighLevel connection failed. The integration was partially successful but couldn't complete the setup. Please try again.`);
      } else {
        alert(`Connection failed: ${message || error}`);
      }
      
      // Clean up URL parameters and stop processing to prevent API calls
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Don't make any API calls during OAuth error - this prevents 401 logout
      // If we have user context, the user should remain logged in
      if (userContext) {
        console.log(`OAuth error occurred for user ${userContext} but session preserved`);
      }
      
      setLoading(false);
      return;
    }
    
    // Handle both old and new callback formats
    const authToken = accessToken || oauthToken || googleToken;
    
    // Special handling for Google Sign-In token
    if (googleToken) {
      console.log('≡ƒöÉ Google Sign-In token received');
      localStorage.setItem('access_token', googleToken);
      fetchUserProfile(googleToken);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    if (authToken || (success === 'true' && (userName || userEmail))) {
      // Handle OAuth callback success - only if we have valid user data
      if (authToken) {
        localStorage.setItem('access_token', authToken);
      }
      
      // Only set user if we have valid data
      if (userName || userEmail) {
        setUser({
          name: userName || 'User',
          email: userEmail || ''
        });
      }
      
      // Show integration success message
      if (success === 'true') {
        alert('Google integration connected successfully!');
        // Trigger integration status refresh by dispatching a custom event
        window.dispatchEvent(new CustomEvent('integrationUpdated', { detail: { provider: 'google' } }));
      }
      if (integrationAdded) {
        alert(`${integrationAdded.replace('_', ' ')} integration added successfully!`);
        // Trigger integration status refresh by dispatching a custom event
        window.dispatchEvent(new CustomEvent('integrationUpdated', { detail: { provider: integrationAdded } }));
      }
      
      // If we have a token, fetch full user profile
      if (authToken) {
        fetchUserProfile(authToken);
      } else {
        setLoading(false);
      }
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    const token = localStorage.getItem('access_token');
    
    if (token) {
      fetchUserProfile(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async (token) => {
    try {
      const response = await api.get('/users/profile');
      setUser(response.data);
      
      // Initialize keyboard shortcuts from server (syncs user's custom shortcuts)
      initializeShortcuts().catch(e => console.warn('Could not sync keyboard shortcuts:', e));
      
      // Trigger recurring task generation (catch-up) after user loads
      triggerRecurringTaskGeneration();
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // If token is invalid, clear it and force re-authentication
      if (error.response?.status === 401) {
        console.log('Token expired or invalid, clearing auth data');
        localStorage.removeItem('access_token');
        setUser(null);
        // The main App component will handle showing login when user is null
      }
    } finally {
      setLoading(false);
    }
  };

  // Trigger recurring task generation (runs silently in background)
  const triggerRecurringTaskGeneration = async () => {
    try {
      const response = await api.post('/recurring/generate', {});
      
      if (response.data.stats && response.data.stats.tasks_generated > 0) {
        console.log(`≡ƒöä Generated ${response.data.stats.tasks_generated} recurring tasks`);
        // Optional: Show a subtle notification
        // You could add a toast notification here if desired
      }
    } catch (error) {
      // Fail silently - don't disrupt user experience if generation fails
      console.error('Recurring task generation error:', error);
    }
  };

  const acceptPendingInvitation = async () => {
    const pendingInvitation = localStorage.getItem('pending_invitation');
    if (!pendingInvitation) return;

    try {
      await api.post(`/team/invitation/${pendingInvitation}/accept`, {});
      localStorage.removeItem('pending_invitation');
    } catch (error) {
      console.error('Failed to accept pending invitation:', error);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      // Include trusted device token if available
      const headers = {};
      const trustedDeviceToken = localStorage.getItem('trusted_device_token');
      if (trustedDeviceToken) {
        headers['x-trusted-device-token'] = trustedDeviceToken;
      }

      // Use the proper login endpoint with password verification
      const response = await api.post('/auth/login', {
        email,
        password,
        remember_me: rememberMe
      }, { headers });
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        return { 
          success: true, 
          mfa_required: true,
          mfa_token: response.data.mfa_token,
          remember_me: rememberMe
        };
      }
      
      const { access_token, user, refresh_token, session_id } = response.data;
      localStorage.setItem('access_token', access_token);
      
      // Store refresh token if "Remember Me" was checked
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('session_id', session_id);
      }
      
      // Fetch full user profile including role
      await fetchUserProfile(access_token);
      await acceptPendingInvitation();
      return { success: true };
    } catch (error) {
      // If password verification fails, show the error
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const completeMFALogin = async (loginData) => {
    const { access_token, trusted_device_token, refresh_token, session_id } = loginData;
    localStorage.setItem('access_token', access_token);
    if (trusted_device_token) {
      localStorage.setItem('trusted_device_token', trusted_device_token);
    }
    // Store refresh token if available (from "Remember Me")
    if (refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('session_id', session_id);
    }
    // Fetch full user profile including role
    await fetchUserProfile(access_token);
    await acceptPendingInvitation();
  };

  const refreshAccessToken = async () => {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      return false;
    }
    
    try {
      const response = await api.post('/auth/refresh', { refresh_token });
      if (response.data.success) {
        localStorage.setItem('access_token', response.data.access_token);
        // Fetch full user profile including role
        await fetchUserProfile(response.data.access_token);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear invalid refresh token
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('session_id');
      return false;
    }
  };

  const register = async (name, email, password, invitationToken = null) => {
    try {
      const requestData = {
        name,
        email,
        password
      };
      
      const url = invitationToken
        ? `/auth/register?invitation_id=${invitationToken}`
        : '/auth/register';
      
      const response = await api.post(url, requestData);
      
      const { access_token, user } = response.data;
      localStorage.setItem('access_token', access_token);
      setUser(user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const loginWithGoogle = async (credentialResponse) => {
    // This will be called with the Google credential response from the popup
    try {
      // Send the Google access token to our backend to verify and create/find user
      const response = await api.post('/auth/google/verify-token', {
        access_token: credentialResponse.access_token
      });
      
      if (response.data.success) {
        const { access_token, user: userData } = response.data;
        localStorage.setItem('access_token', access_token);
        setUser(userData);
        await acceptPendingInvitation();
        return { success: true };
      } else {
        return { success: false, error: response.data.detail || 'Google login failed' };
      }
    } catch (error) {
      console.error('Google login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Google login failed' 
      };
    }
  };

  const logout = async () => {
    // Revoke refresh token on server
    const refresh_token = localStorage.getItem('refresh_token');
    if (refresh_token) {
      try {
        await api.post('/auth/logout', { refresh_token });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    // Clear all auth data
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('session_id');
    localStorage.removeItem('trusted_device_token');
    localStorage.removeItem('navigationState');
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    // Only update user state if the data has actually changed
    setUser(prevUser => {
      // If the new data is the same as current, don't update
      if (JSON.stringify(prevUser) === JSON.stringify(updatedUserData)) {
        return prevUser;
      }
      return updatedUserData;
    });
  };

  return { user, loading, login, register, loginWithGoogle, logout, updateUser, completeMFALogin };
};

// Login/Register component
const Auth = ({ onLogin, onRegister, onGoogleLogin, onMFAComplete }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invitationToken, setInvitationToken] = useState(null);
  const [invitationValid, setInvitationValid] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [pendingRememberMe, setPendingRememberMe] = useState(false);

  // Google OAuth popup login
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      setError('');
      try {
        const result = await onGoogleLogin(tokenResponse);
        if (!result.success) {
          setError(result.error || 'Google login failed');
        }
      } catch (err) {
        setError('Google login failed. Please try again.');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google login error:', error);
      setError('Google login failed. Please try again.');
      setGoogleLoading(false);
    },
    flow: 'implicit', // Use implicit flow for simpler token handling
  });

  // Check for invitation token in URL params on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const invitationId = urlParams.get('invitation') || urlParams.get('token');
    const email = urlParams.get('email');
    
    if (invitationId) {
      setInvitationToken(invitationId);
      if (email) {
        setFormData(prev => ({ ...prev, email }));
      }
      setIsLogin(false); // Switch to registration mode
      
      // Validate invitation token
      validateInvitationToken(invitationId);
    }
    
    // Check for session expired notification
    const sessionExpired = urlParams.get('session_expired');
    if (sessionExpired === 'true') {
      setError('Your session has expired. Please sign in again.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const validateInvitationToken = async (invitationId) => {
    try {
      const response = await api.get(`/invitations/${invitationId}/validate`);
      
      if (response.data.valid) {
        setInvitationValid(true);
        if (response.data.email) {
          setFormData(prev => ({
            ...prev,
            email: response.data.email
          }));
        }
      } else {
        setInvitationValid(false);
        setError(response.data.message || 'Invalid invitation token');
      }
    } catch (error) {
      setInvitationValid(false);
      setError('Failed to validate invitation token');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    let result;
    if (isLogin) {
      result = await onLogin(formData.email, formData.password, rememberMe);
      
      // Check if MFA is required
      if (result.success && result.mfa_required) {
        setMfaToken(result.mfa_token);
        setPendingRememberMe(result.remember_me); // Save for after MFA
        setMfaRequired(true);
        setLoading(false);
        return;
      }
    } else {
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        setLoading(false);
        return;
      }
      result = await onRegister(formData.name, formData.email, formData.password, invitationToken);
    }

    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleMFASuccess = (loginData) => {
    if (onMFAComplete) {
      onMFAComplete(loginData);
    }
  };

  const handleMFACancel = () => {
    setMfaRequired(false);
    setMfaToken(null);
    setPendingRememberMe(false);
    setFormData(prev => ({ ...prev, password: '' }));
  };

  // Show MFA verification screen if MFA is required
  if (mfaRequired && mfaToken) {
    return (
      <MFAVerify
        mfaToken={mfaToken}
        rememberMe={pendingRememberMe}
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />
    );
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError(''); // Clear error when user starts typing
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full border border-white/20">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img 
              src="/images/athena-logo-wide.png" 
              alt="Athena" 
              className="h-16 w-auto object-contain"
            />
          </div>
          <p className="text-gray-300">Intelligent Project Management</p>
        </div>

        <div className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          {/* Auth Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Remember Me and Forgot Password row */}
              <div className="flex items-center justify-between mt-3">
                {isLogin && (
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-white/30 bg-white/10 text-purple-500 focus:ring-purple-500 focus:ring-offset-0"
                    />
                    <span className="ml-2 text-sm text-gray-300 group-hover:text-white">
                      Stay signed in for 30 days
                    </span>
                  </label>
                )}
                <a
                  href="/reset-password"
                  className="text-sm text-purple-400 hover:text-purple-300 underline"
                >
                  Forgot password?
                </a>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Signing In...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center space-x-4">
            <div className="flex-1 h-px bg-white/20"></div>
            <span className="text-gray-400 text-sm">or</span>
            <div className="flex-1 h-px bg-white/20"></div>
          </div>

          {/* Google OAuth */}
          <button
            onClick={() => googleLogin()}
            disabled={googleLoading}
            className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {googleLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                <span>Signing in with Google...</span>
              </div>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </>
            )}
          </button>

          <div className="text-center text-xs text-gray-400">
            <p>
              Don't have an account?{' '}
              <a
                href="/pricing"
                className="text-purple-400 hover:text-purple-300 underline"
              >
                Sign up here
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sidebar component
const Sidebar = ({ onDashboard, onSchedule, onInbox, onTaskHub, onSettings, onGovernance, governanceAccess, user, onLogout, currentView, organizationName, onOrganizationView, organizationView, onNotifications, onUserProfile, currentOrganization, onOrganizationChange, badgeCounts = {}, isMessagingView = false }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  return (
    <div className={`bg-slate-900 text-white transition-all duration-300 flex flex-col flex-shrink-0 overflow-y-auto ${isCollapsed ? 'w-20' : 'w-80'} border-r border-slate-700 ${isMessagingView ? 'h-full min-h-0' : 'min-h-screen'}`}>
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          {!isCollapsed ? (
            <div className="flex items-center">
              <img 
                src="/images/athena-logo-wide.png" 
                alt="Athena" 
                className="h-10 w-auto object-contain"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center w-full">
              <img 
                src="/images/athena-logo.png" 
                alt="Athena" 
                className="w-8 h-8 object-contain"
              />
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="p-4 border-b border-slate-700">
          <div className="relative">
            <div className="flex items-center space-x-2 mb-4">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 flex-1 hover:bg-slate-800 rounded-lg p-2 transition-colors"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  {user?.avatar_url && user.avatar_url.trim() !== '' && !user.avatar_url.includes('example.com') ? (
                    <img 
                      src={user.avatar_url} 
                      alt={user.name} 
                      className="w-10 h-10 rounded-full object-cover"
                      onError={(e) => {
                        // Hide broken image and show initials
                        e.target.style.display = 'none';
                        const initialsSpan = e.target.nextElementSibling;
                        if (initialsSpan) {
                          initialsSpan.style.display = 'inline-block';
                        }
                      }}
                    />
                  ) : null}
                  <span 
                    className="text-white font-semibold text-sm"
                    style={{ display: user?.avatar_url && user.avatar_url.trim() !== '' && !user.avatar_url.includes('example.com') ? 'none' : 'inline-block' }}
                  >
                    {getUserInitials(user?.name)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                </div>
              </button>
              
              {/* Notification Bell - moved outside user button */}
              <div className="relative">
                <button
                  onClick={onNotifications}
                  className="relative p-2 text-gray-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <Bell className="w-4 h-4" />
                  <Badge count={badgeCounts.notifications} className="absolute -top-1 -right-1" />
                </button>
              </div>

            </div>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-lg z-50">
                <button
                  onClick={() => {
                    onUserProfile();
                    setShowUserMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-slate-700 text-gray-300 hover:text-white transition-colors text-left"
                >
                  <User className="w-4 h-4" />
                  <span>Profile</span>
                </button>
                <button
                  onClick={() => {
                    onSettings();
                    setShowUserMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-slate-700 text-gray-300 hover:text-white transition-colors text-left"
                >
                  <SettingsIcon className="w-4 h-4" />
                  <span>Settings</span>
                </button>
                <div className="border-t border-slate-600"></div>
                <button
                  onClick={() => {
                    onLogout();
                    setShowUserMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-red-600 text-gray-300 hover:text-white transition-colors text-left"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <button
              onClick={onDashboard}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                currentView === 'dashboard' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span>Dashboard</span>
            </button>
            
            <button
              onClick={onSchedule}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                currentView === 'schedule' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
              }`}
            >
              <Calendar className="w-5 h-5" />
              <span>Schedule</span>
            </button>
            
            <button
              onClick={onTaskHub}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                currentView === 'task-hub' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
              }`}
            >
              <CheckSquare className="w-5 h-5" />
              <span>Task Hub</span>
            </button>

            <button
              onClick={() => onOrganizationView('companies')}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                currentView === 'organization' && organizationView === 'companies' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
              }`}
            >
              <Building2 className="w-5 h-5" />
              <span>Companies</span>
            </button>

            <button
              onClick={() => onOrganizationView('contacts')}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                currentView === 'organization' && organizationView === 'contacts' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
              }`}
            >
              <Users className="w-5 h-5" />
              <span>Contacts</span>
            </button>
            
            <button
              onClick={() => onOrganizationView('messaging')}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-all text-left ${
                currentView === 'organization' && organizationView === 'messaging' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
              }`}
            >
              <div className="flex items-center space-x-3">
                <MessageSquare className="w-5 h-5" />
                <span>Internal Messaging</span>
              </div>
              <Badge count={badgeCounts.internal_messages} />
            </button>
            
            <button
              onClick={() => onOrganizationView('personal')}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                currentView === 'organization' && organizationView === 'personal' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
              }`}
            >
              <UserCheck className="w-5 h-5" />
              <span>Personal Tasks</span>
            </button>
            
            {/* Governance - Only show if user has access */}
            {governanceAccess?.show_governance_nav && (
              <button
                onClick={onGovernance}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                  currentView === 'governance' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
                }`}
              >
                <Gavel className="w-5 h-5" />
                <span>Governance</span>
              </button>
            )}
            
            <button
              onClick={onSettings}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                currentView === 'settings' ? 'bg-gradient-to-r from-purple-600 to-pink-600' : 'hover:bg-slate-800'
              }`}
            >
              <SettingsIcon className="w-5 h-5" />
              <span>Settings</span>
            </button>
          </div>
        </div>
      )}

      {/* Organization Section */}
      {!isCollapsed && (
        <div className="mt-8">
          <OrganizationHeader
            organizationName={organizationName}
            currentOrganization={currentOrganization}
            onOrganizationChange={onOrganizationChange}
          />
          
          <div className="space-y-1 px-2">
            <button
              onClick={() => onOrganizationView('projects')}
              className={`group flex items-center space-x-3 px-4 py-2 text-white rounded-lg transition-all w-full ${
                currentView === 'organization' && organizationView === 'projects' ? 'bg-gradient-to-r from-purple-600 to-pink-600 shadow-md' : 'hover:bg-slate-800'
              }`}
            >
              <FolderOpen className="w-5 h-5" />
              <span>Company Projects</span>
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 p-4 bg-slate-900" />

      {!isCollapsed && (
        <div className="p-4 border-t border-slate-700">
        </div>
      )}
    </div>
  );
};

// Project form modal
const ProjectForm = ({ project, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: project?.name || '',
    description: project?.description || '',
    status: project?.status || 'open',
    due_date: project?.due_date ? new Date(project.due_date).toISOString().split('T')[0] : '',
    budget: project?.budget || '',
    client_reference: project?.client_reference || '',
    priority: project?.priority || 'normal',
    category: project?.category || '',
    scope_of_work: project?.scope_of_work || '',
    process_scope_with_ai: false,
    template_id: null  // Track which template was used
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [taskCategories, setTaskCategories] = useState([]);
  
  // Template selection states
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [projectTemplates, setProjectTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  // Fetch task categories for project category dropdown
  const fetchTaskCategories = async () => {
    try {
      const response = await api.get('/task-categories');
      if (response.data && Array.isArray(response.data)) {
        setTaskCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch task categories:', error);
    }
  };

  // Fetch project templates
  const fetchProjectTemplates = async () => {
    try {
      console.log('Fetching project templates...');
      const response = await api.get('/templates/projects');
      console.log('Project templates response:', response.data);
      setProjectTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch project templates:', error);
      console.error('Error details:', error.response?.data);
      setProjectTemplates([]);
    }
  };

  // Apply template to form (only fill empty fields)
  const applyTemplate = (template) => {
    setFormData(prevData => ({
      ...prevData,
      name: prevData.name || template.name || '',
      description: prevData.description || template.description || '',
      budget: prevData.budget || template.estimated_duration_days || '',
      // Don't prefill other fields to preserve user choices
      template_id: template.id
    }));
    setShowTemplateModal(false);
    setSelectedTemplateId(template.id);
  };

  // Load templates and categories when component mounts
  React.useEffect(() => {
    if (!project) {
      fetchProjectTemplates();
    }
    fetchTaskCategories();
  }, [project]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      
      // Read text content for text files
      if (file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          setFormData({...formData, scope_of_work: event.target.result});
        };
        reader.readAsText(file);
      } else {
        // For other file types, just set the filename
        setFormData({...formData, scope_of_work: `[Uploaded file: ${file.name}]`});
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const data = { ...formData };
      if (data.due_date) {
        data.due_date = new Date(data.due_date).toISOString();
      }
      
      // If AI processing is enabled and we have scope of work
      if (data.process_scope_with_ai && data.scope_of_work) {
        data.process_with_ai = true;
      }

      await onSave(data);
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">
            {project ? 'Edit Project' : 'Create New Project'}
          </h2>
          {!project && (
            <button
              type="button"
              onClick={() => setShowTemplateModal(true)}
              className="flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Use Template
            </button>
          )}
        </div>
        
        {/* Template Selection Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-900">Select Project Template</h3>
                <p className="text-sm text-gray-600 mt-1">Choose a template to prefill your project</p>
                <p className="text-xs text-gray-500 mt-1">Found {projectTemplates.length} templates</p>
              </div>
              
              <div className="overflow-y-auto max-h-[60vh] p-6">
                {projectTemplates.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>No project templates available</p>
                    <p className="text-sm mt-2">Create templates in Settings to use them here</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {projectTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => applyTemplate(template)}
                        className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{template.name}</h4>
                            {template.description && (
                              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                            )}
                            <div className="flex flex-wrap gap-2 mt-2">
                              {template.category && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                  {template.category}
                                </span>
                              )}
                              {template.estimated_duration_days && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                  {template.estimated_duration_days}
                                </span>
                              )}
                              {template.stages && Array.isArray(template.stages) && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                                  {template.stages.length} stages
                                </span>
                              )}
                              {template.default_tasks && Array.isArray(template.default_tasks) && template.default_tasks.length > 0 && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                                  {template.default_tasks.length} tasks
                                </span>
                              )}
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Project Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 placeholder-gray-500"
                placeholder="Enter project name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Reference</label>
              <input
                type="text"
                value={formData.client_reference}
                onChange={(e) => setFormData({...formData, client_reference: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 placeholder-gray-500"
                placeholder="Client project reference #"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 placeholder-gray-500"
              rows="3"
              placeholder="Brief overview of the project..."
            />
          </div>

          {/* Project Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900"
              >
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="on-hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({...formData, priority: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900"
              >
                <option value="">No Category</option>
                {taskCategories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Budget (Optional)</label>
            <input
              type="text"
              value={formData.budget}
              onChange={(e) => setFormData({...formData, budget: e.target.value})}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 placeholder-gray-500"
              placeholder="e.g., $10,000 or 40 hours"
            />
          </div>

          {/* Scope of Work Section */}
          <div className="border-t pt-6">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-medium text-gray-900">Scope of Work</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Scope & Requirements
                </label>
                <div className="space-y-3">
                  <textarea
                    value={formData.scope_of_work}
                    onChange={(e) => setFormData({...formData, scope_of_work: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 placeholder-gray-500"
                    rows="8"
                    placeholder="Paste or type the detailed scope of work, requirements, deliverables, and specifications here...

Example:
- Develop a responsive website with 5 pages
- Integrate payment processing system
- Set up user authentication
- Create admin dashboard
- Implement email notifications
- Conduct testing and deployment"
                  />
                  
                  {/* File Upload Option */}
                  <div className="flex items-center space-x-4">
                    <div>
                      <input
                        type="file"
                        id="scope-file"
                        onChange={handleFileUpload}
                        accept=".txt,.md,.doc,.docx,.pdf"
                        className="hidden"
                      />
                      <label
                        htmlFor="scope-file"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Scope File
                      </label>
                    </div>
                    {uploadedFile && (
                      <span className="text-sm text-gray-600">
                        Uploaded: {uploadedFile.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* AI Processing Option - Always visible with better call-to-action */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="ai-processing"
                    checked={formData.process_scope_with_ai}
                    onChange={(e) => setFormData({...formData, process_scope_with_ai: e.target.checked})}
                    className="mt-1 w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    disabled={!formData.scope_of_work.trim()}
                  />
                  <div className="flex-1">
                    <label htmlFor="ai-processing" className="font-semibold text-purple-900 cursor-pointer text-base">
                      Generate Tasks & Subtasks with Athena AI
                    </label>
                    <p className="text-sm text-purple-700 mt-1">
                      {formData.scope_of_work.trim() ? 
                        "Automatically break down the scope of work into organized tasks and subtasks with intelligent assignments, priorities, and timelines." :
                        "Add scope of work content above to enable AI task generation."
                      }
                    </p>
                    <div className="text-xs text-purple-600 mt-2 space-y-1">
                      <div>- Smart task breakdown and organization</div>
                      <div>- Automatic subtask creation</div>
                      <div>- Priority and timeline suggestions</div>
                      <div>- Team member assignment recommendations</div>
                    </div>
                    {formData.process_scope_with_ai && (
                      <div className="mt-3 p-2 bg-white rounded border border-purple-300">
                        <div className="text-sm text-purple-800 font-medium">
                          AI Processing Enabled
                        </div>
                        <div className="text-xs text-purple-600">
                          Tasks will be automatically generated after project creation
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  {formData.process_scope_with_ai ? 'Creating & Processing...' : 'Creating...'}
                </span>
              ) : (
                project ? 'Update Project' : 'Create Project'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Dashboard component  
const Dashboard = ({ stats, onQuickAddTask, clients, onRecentProjectClick, teamMembers = [], onTaskClick, user, upcomingMeetings = [] }) => {
  const [showQuickTaskModal, setShowQuickTaskModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [dashboardTasks, setDashboardTasks] = useState(null);

  // Timer states
  const [currentTimer, setCurrentTimer] = useState(null);
  const [timerDuration, setTimerDuration] = useState(0);

  // AI Task Generation Approval Queue states
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [approvingTasks, setApprovingTasks] = useState(new Set());

  // Fetch user's dashboard tasks and approval queue
  useEffect(() => {
    fetchDashboardTasks();
    fetchApprovalQueue();
  }, []);

  // Fetch current timer status
  useEffect(() => {
    const fetchTimerStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        const response = await api.get('/timer/status');
        if (response.data) {
          setCurrentTimer(response.data);
        }
      } catch (error) {
        console.log('No active timer or error fetching timer status');
      }
    };
    
    fetchTimerStatus();
  }, []);

  // Update timer duration every second
  useEffect(() => {
    let interval;
    if (currentTimer?.is_running) {
      interval = setInterval(() => {
        const now = new Date();
        const startTime = new Date(currentTimer.start_time);
        const diffMinutes = Math.floor((now - startTime) / (1000 * 60));
        setTimerDuration(diffMinutes);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentTimer]);

  const fetchDashboardTasks = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('No auth token found for dashboard tasks');
        return;
      }
      
      const response = await api.get('/dashboard/tasks');
      setDashboardTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard tasks:', error);
      // If authentication failed, the token might be invalid
      if (error.response?.status === 401) {
        console.log('Dashboard tasks: Token expired or invalid');
        // Don't clear token here as it might affect other components
        // Let the main profile fetch handle token clearing
      }
    }
  };

  const fetchApprovalQueue = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('No auth token found for approval queue');
        return;
      }
      
      const response = await api.get('/ai-task-generation/approval-queue');
      
      if (response.data.success) {
        setApprovalQueue(response.data.pending_approvals || []);
      }
    } catch (error) {
      console.error('Failed to load approval queue:', error);
      if (error.response?.status === 401) {
        console.log('Approval queue: Token expired or invalid');
      }
    }
  };

  const handleApproveTaskGeneration = async (generationId) => {
    try {
      setApprovingTasks(prev => new Set(prev).add(generationId));
      
      await api.post(`/ai-task-generation/approve/${generationId}`, {});
      
      // Refresh approval queue
      await fetchApprovalQueue();
    } catch (error) {
      console.error('Failed to approve task generation:', error);
      alert('Failed to approve tasks. Please try again.');
    } finally {
      setApprovingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(generationId);
        return newSet;
      });
    }
  };

  // Timer functionality for dashboard tasks
  const handleDashboardTaskTimer = async (taskId) => {
    try {
      if (currentTimer?.is_running) {
        // Stop current timer
        await api.post('/timer', {
          action: 'stop',
          task_id: currentTimer.task_id
        });
        setCurrentTimer(null);
        setTimerDuration(0);
      }
      
      // Start new timer if it's a different task or no timer was running
      if (!currentTimer?.is_running || currentTimer?.task_id !== taskId) {
        const response = await api.post('/timer', {
          action: 'start',
          task_id: taskId
        });
        
        setCurrentTimer(response.data);
      }
    } catch (error) {
      console.error('Timer error:', error);
      alert('Error with timer. Please try again.');
    }
  };

  const handleTaskCreated = (newTask) => {
    // Refresh dashboard tasks after creating a new one
    fetchDashboardTasks();
  };

  const handleOpenFullEditor = (formData) => {
    // Handle opening full task editor with pre-filled data
    console.log('Opening full editor with data:', formData);
    // You can implement this to open the TaskModal with pre-filled data
  };

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8 bg-gray-50 pb-24 w-full overflow-x-hidden">
      <div className="w-full max-w-full">
        {/* Timer Widget - Show if timer is running */}
        {currentTimer?.is_running && (
          <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-green-800">
                    {Math.floor(timerDuration / 60)}h {timerDuration % 60}m
                  </div>
                  <div className="text-sm text-green-600">
                    {dashboardTasks?.assigned_tasks?.find(t => t.id === currentTimer.task_id)?.name || 'Active Timer'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleDashboardTaskTimer(currentTimer.task_id)}
                className="flex items-center space-x-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Square className="w-4 h-4" />
                <span>Stop</span>
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <button
            onClick={() => setShowQuickTaskModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Quick Add Task</span>
          </button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Clients</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.total_clients || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-800 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-300" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Tasks</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{dashboardTasks?.assigned_tasks?.length || 0}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-800 rounded-xl flex items-center justify-center">
                <CheckSquare className="w-6 h-6 text-orange-600 dark:text-orange-300" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Projects</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.open_projects || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-800 rounded-xl flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-purple-600 dark:text-purple-300" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-900 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">This Week's Meetings</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats?.upcoming_meetings || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-800 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-green-600 dark:text-green-300" />
              </div>
            </div>
          </div>
        </div>
        
        {/* AI Task Generation Approval Queue */}
        <div className="mb-8">
          {approvalQueue.length > 0 ? (
            /* Full widget when there are pending approvals */
            <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border-2 border-orange-200 dark:border-orange-800 rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <Zap className="w-5 h-5 text-orange-600" />
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-orange-900 dark:text-orange-100">AI Task Approval Required</h2>
                    <p className="text-sm text-orange-700 dark:text-orange-200">
                      {approvalQueue.length} AI-generated task{approvalQueue.length === 1 ? '' : 's'} waiting for approval
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-3 py-1 bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200 rounded-full text-sm font-medium">
                    {approvalQueue.length} Pending
                  </span>
                </div>
              </div>
              
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {approvalQueue.slice(0, 3).map((item) => (
                  <div key={item.approval_id || item.generation_id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-orange-200 dark:border-orange-700">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {item.generated_tasks_count} tasks generated
                        </h4>
                        {item.priority && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.priority === 'high' || item.priority === 'urgent'
                              ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200' 
                              : 'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200'
                          }`}>
                            {item.priority} priority
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                        From: {item.source_content_id} ΓÇó {item.submitted_at ? new Date(item.submitted_at).toLocaleDateString() : 'Unknown date'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleApproveTaskGeneration(item.generation_id)}
                        disabled={approvingTasks.has(item.generation_id)}
                        className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>{approvingTasks.has(item.generation_id) ? 'Approving...' : 'Approve'}</span>
                      </button>
                      <button 
                        className="flex items-center space-x-1 px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                        onClick={() => {
                          // TODO: Add review functionality
                          console.log('Review task generation:', item);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                        <span>Review</span>
                      </button>
                    </div>
                  </div>
                ))}
                
                {approvalQueue.length > 3 && (
                  <div className="text-center pt-3 border-t border-orange-200 dark:border-orange-700">
                    <p className="text-sm text-orange-700 dark:text-orange-300">
                      ...and {approvalQueue.length - 3} more waiting for approval
                    </p>
                    <button 
                      className="mt-2 text-sm text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200 font-medium"
                      onClick={() => {
                        // TODO: Navigate to full approval queue in settings
                        console.log('View all approvals');
                      }}
                    >
                      View All Approvals ΓåÆ
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Compact widget when empty */
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">AI Task Generation</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">No pending approvals</p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                  All Clear
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Meetings and Projects Module */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Upcoming Meetings - Now Primary Section */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-purple-600" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upcoming Meetings</h2>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Next 7 days</span>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {/* Real meetings data from API */}
              {upcomingMeetings.length > 0 ? (
                upcomingMeetings.map(meeting => (
                  <div 
                    key={meeting.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-black rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                    onClick={() => {
                      console.log('Meeting clicked:', meeting);
                      setSelectedMeeting(meeting);
                      setShowMeetingModal(true);
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">{meeting.title || 'Untitled Meeting'}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                        {meeting.client_name || 'No Client'} | {meeting.start_time ? new Date(meeting.start_time).toLocaleDateString() : 'No Date'}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200">
                        {meeting.platform || 'Unknown'}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{meeting.duration || '1h'}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-gray-300 dark:text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 mb-2">No upcoming meetings</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Your schedule is clear for the next 7 days</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions & Upcoming Tasks */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <CheckSquare className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upcoming Tasks</h2>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Ordered by due date</span>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {dashboardTasks?.assigned_tasks?.length > 0 ? (
                dashboardTasks.assigned_tasks
                  .slice(0, 6)
                  .map(task => (
                  <div 
                    key={task.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-black rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => {
                        console.log('Task clicked:', task);
                        if (onTaskClick) {
                          onTaskClick(task);
                        }
                      }}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-medium text-gray-900 dark:text-white truncate">{task.name}</h3>
                        {currentTimer?.task_id === task.id && (
                          <div className="flex items-center space-x-1 px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded-full">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-xs font-medium">
                              {Math.floor(timerDuration / 60)}h {timerDuration % 60}m
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                        {task.project_name && <span>{task.project_name} | </span>}
                        {task.priority} priority
                        {task.due_date && (
                          <span className="ml-2">| Due {new Date(task.due_date).toLocaleDateString()}</span>
                        )}
                        {task.due_time && (
                          <span className="ml-2">| {task.due_time}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {/* Timer Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDashboardTaskTimer(task.id);
                        }}
                        className={`p-1 rounded transition-colors ${
                          currentTimer?.task_id === task.id
                            ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                        }`}
                        title={currentTimer?.task_id === task.id ? 'Stop Timer' : 'Start Timer'}
                      >
                        {currentTimer?.task_id === task.id ? (
                          <Square className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      
                      {/* Status Badge */}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        task.status === 'To Do' 
                          ? 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200'
                          : task.status === 'In Progress'
                          ? 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                          : task.status === 'Blocked'
                          ? 'bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200'
                          : task.status === 'In Review'
                          ? 'bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200'
                          : 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                      }`}>
                        {task.status}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CheckSquare className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No open tasks</p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">All tasks are completed!</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Projects</h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">User projects only</span>
          </div>
          <div className="space-y-4">
            {stats?.recent_projects?.length > 0 ? (
              stats.recent_projects
                .filter(project => {
                  // Enhanced filter to remove all general/default/system projects
                  const projectName = project.name.toLowerCase();
                  const isGeneralProject = 
                    projectName.includes('general') ||
                    projectName.includes('default') ||
                    projectName.includes('system') ||
                    projectName.includes('sample') ||
                    projectName.includes('template') ||
                    projectName.includes('test') ||
                    project.name === 'General Project' ||
                    project.name === 'Default Project' ||
                    project.name === 'Sample Project' ||
                    project.name === 'Test Project';
                  
                  return !isGeneralProject;
                })
                .map(project => (
                <div 
                  key={project.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-black rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  onClick={() => onRecentProjectClick(project)}
                >
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">{project.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{project.description || 'No description'}</p>
                    {project.client_name && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">Client: {project.client_name}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-3 ml-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project.status === 'open' 
                        ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200' 
                        : project.status === 'in_progress'
                        ? 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                        : 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200'
                    }`}>
                      {project.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">No recent user projects</p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">Create a project to see it here</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Add Task Modal */}
      <QuickAddTaskModal
        isOpen={showQuickTaskModal}
        onClose={() => setShowQuickTaskModal(false)}
        onTaskCreated={handleTaskCreated}
        onOpenFullEditor={handleOpenFullEditor}
        user={user}
      />

      {/* Meeting Details Modal - Enhanced */}
      {showMeetingModal && selectedMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  selectedMeeting.type === 'client' ? 'bg-purple-600' : 'bg-blue-600'
                }`}>
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">Meeting Details</h2>
              </div>
              <button
                onClick={() => setShowMeetingModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Meeting Title */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedMeeting.title}</h3>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedMeeting.type === 'client' 
                      ? 'bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200'
                      : 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                  }`}>
                    {selectedMeeting.type} meeting
                  </span>
                </div>
              </div>

              {/* Meeting Info Grid */}
              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-start space-x-3">
                  <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Client/Team</p>
                    <p className="text-sm text-gray-600">{selectedMeeting.client}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Time & Duration</p>
                    <p className="text-sm text-gray-600">{selectedMeeting.time}</p>
                    <p className="text-xs text-gray-500">{selectedMeeting.duration}</p>
                  </div>
                </div>

                {selectedMeeting.location && (
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Location</p>
                      <p className="text-sm text-gray-600">{selectedMeeting.location}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Attendees</p>
                    <p className="text-sm text-gray-600">{selectedMeeting.attendees} people expected</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedMeeting.description && (
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-2">Description</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">{selectedMeeting.description}</p>
                  </div>
                </div>
              )}

              {/* Agenda */}
              {selectedMeeting.agenda && selectedMeeting.agenda.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-3">Meeting Agenda</p>
                  <div className="space-y-2">
                    {selectedMeeting.agenda.map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-medium mt-0.5">
                          {index + 1}
                        </div>
                        <p className="text-sm text-gray-700 flex-1">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowMeetingModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Join Meeting
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CompaniesCRMView = ({ clients = [], contacts = [], crmTasks = [], onViewProjects, onViewTasks, onOpenSettings, onContactDeleted, onCompanyCreated, onContactCreated, initialTab = 'companies' }) => {
  const [tab, setTab] = useState(initialTab === 'contacts' ? 'contacts' : 'companies');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState([]);
  const [sortBy, setSortBy] = useState('alphabetical');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactCompanies, setContactCompanies] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', role: '', title: '' });
  const [associationCompanyId, setAssociationCompanyId] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingContact, setDeletingContact] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [savingNewRecord, setSavingNewRecord] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({
    name: '',
    contacts: [{ name: '', email: '', phone: '', role: 'Primary Contact', is_primary: true }]
  });
  const [newContactForm, setNewContactForm] = useState({
    company_id: '',
    name: '',
    email: '',
    phone: '',
    role: 'Contact',
    title: '',
    department: '',
    is_primary: false,
  });

  useEffect(() => {
    setTab(initialTab === 'contacts' ? 'contacts' : 'companies');
  }, [initialTab]);

  useEffect(() => {
    setPage(1);
  }, [tab, search, filters, sortBy, pageSize]);

  const toggleFilter = (filter) => {
    setFilters(prev => prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]);
  };

  const openContactProfile = async (contact) => {
    setSelectedContact(contact);
    setContactForm({
      name: contact?.name || '',
      email: contact?.email || '',
      phone: contact?.phone || '',
      role: contact?.role || '',
      title: contact?.title || '',
    });
    setAssociationCompanyId('');
    setDeleteConfirmText('');

    try {
      const res = await api.get(`${API_V1_BASE}/api/v1/contacts/${contact.id}/companies`);
      setContactCompanies(res.data?.data || []);
    } catch (e) {
      setContactCompanies([]);
    }
  };

  const saveContactProfile = async () => {
    if (!selectedContact?.id) return;
    try {
      setProfileSaving(true);
      await api.patch(`${API_V1_BASE}/api/v1/contacts/${selectedContact.id}`, contactForm);
      setSelectedContact(prev => ({ ...prev, ...contactForm }));
    } finally {
      setProfileSaving(false);
    }
  };

  const addAssociation = async () => {
    if (!selectedContact?.id || !associationCompanyId) return;
    await api.post(`${API_V1_BASE}/api/v1/contacts/${selectedContact.id}/companies/${associationCompanyId}`);
    const res = await api.get(`${API_V1_BASE}/api/v1/contacts/${selectedContact.id}/companies`);
    setContactCompanies(res.data?.data || []);
    setAssociationCompanyId('');
  };

  const removeAssociation = async (companyId) => {
    if (!selectedContact?.id) return;
    await api.delete(`${API_V1_BASE}/api/v1/contacts/${selectedContact.id}/companies/${companyId}`);
    setContactCompanies(prev => prev.filter(c => c.company_id !== companyId));
  };

  const deleteContactProfile = async () => {
    if (!selectedContact?.id) return;
    if (String(deleteConfirmText || '').trim().toLowerCase() !== 'delete') {
      alert('Type DELETE to confirm contact removal.');
      return;
    }

    try {
      setDeletingContact(true);
      await api.delete(`${API_V1_BASE}/api/v1/contacts/${selectedContact.id}`);
      const deletedId = selectedContact.id;
      setSelectedContact(null);
      setDeleteConfirmText('');
      if (onContactDeleted) {
        await onContactDeleted(deletedId);
      }
    } finally {
      setDeletingContact(false);
    }
  };

  const addCompanyContactRow = () => {
    setNewCompanyForm(prev => ({
      ...prev,
      contacts: [...prev.contacts, { name: '', email: '', phone: '', role: 'Contact', is_primary: false }]
    }));
  };

  const updateCompanyContactRow = (index, key, value) => {
    setNewCompanyForm(prev => {
      const next = [...prev.contacts];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, contacts: next };
    });
  };

  const removeCompanyContactRow = (index) => {
    setNewCompanyForm(prev => {
      if (prev.contacts.length <= 1) return prev;
      const next = prev.contacts.filter((_, i) => i !== index);
      if (!next.some(c => c.is_primary)) {
        next[0] = { ...next[0], is_primary: true };
      }
      return { ...prev, contacts: next };
    });
  };

  const createCompany = async () => {
    const companyName = String(newCompanyForm.name || '').trim();
    const validContacts = (newCompanyForm.contacts || []).filter(c => String(c.name || '').trim());

    if (!companyName) {
      alert('Company name is required.');
      return;
    }
    if (validContacts.length < 1) {
      alert('At least one contact is required when creating a company.');
      return;
    }

    try {
      setSavingNewRecord(true);
      await api.post(`${API_V1_BASE}/api/v1/companies`, {
        name: companyName,
        contacts: validContacts,
      });

      setShowAddCompanyModal(false);
      setNewCompanyForm({
        name: '',
        contacts: [{ name: '', email: '', phone: '', role: 'Primary Contact', is_primary: true }]
      });
      if (onCompanyCreated) await onCompanyCreated();
      if (onContactCreated) await onContactCreated();
    } catch (error) {
      alert(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to create company.');
    } finally {
      setSavingNewRecord(false);
    }
  };

  const createContact = async () => {
    const payload = {
      ...newContactForm,
      name: String(newContactForm.name || '').trim(),
    };

    if (!payload.company_id) {
      alert('Please select a company.');
      return;
    }
    if (!payload.name) {
      alert('Contact name is required.');
      return;
    }

    try {
      setSavingNewRecord(true);
      await api.post(`${API_V1_BASE}/api/v1/contacts`, payload);
      setShowAddContactModal(false);
      setNewContactForm({ company_id: '', name: '', email: '', phone: '', role: 'Contact', title: '', department: '', is_primary: false });
      if (onContactCreated) await onContactCreated();
    } catch (error) {
      alert(error?.response?.data?.detail?.message || error?.response?.data?.detail || 'Failed to create contact.');
    } finally {
      setSavingNewRecord(false);
    }
  };

  const contactsByCompany = useMemo(() => {
    const map = new Map();
    (contacts || []).forEach(c => {
      const companyId = c?.company_id;
      if (!companyId) return;
      if (!map.has(companyId)) map.set(companyId, []);
      map.get(companyId).push(c);
    });
    return map;
  }, [contacts]);

  const openTaskCountByCompany = useMemo(() => {
    const doneWords = ['complete', 'completed', 'done'];
    const map = new Map();
    (crmTasks || []).forEach(task => {
      const companyId = task?.client_id;
      if (!companyId) return;
      const status = String(task?.status || '').toLowerCase();
      const isOpen = !doneWords.some(word => status.includes(word));
      if (!isOpen) return;
      map.set(companyId, (map.get(companyId) || 0) + 1);
    });
    return map;
  }, [crmTasks]);

  const rows = useMemo(() => {
    const s = search.toLowerCase();
    if (tab === 'companies') {
      const filtered = (clients || []).filter(company => {
        const matchesSearch = !s || company?.name?.toLowerCase().includes(s);
        const openTaskCount = openTaskCountByCompany.get(company.id) || 0;
        const matchesOpenTasks = !filters.includes('openTasks') || openTaskCount > 0;
        return matchesSearch && matchesOpenTasks;
      });

      const sorted = [...filtered];
      if (sortBy === 'open_tasks') {
        sorted.sort((a, b) => (openTaskCountByCompany.get(b.id) || 0) - (openTaskCountByCompany.get(a.id) || 0));
      } else if (sortBy === 'created_desc') {
        sorted.sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
      } else {
        sorted.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
      }
      return sorted;
    }

    return (contacts || []).filter(contact => {
      const companyName = (clients || []).find(c => c.id === contact.company_id)?.name || '';
      return !s ||
        contact?.name?.toLowerCase().includes(s) ||
        (contact?.email && contact.email.toLowerCase().includes(s)) ||
        companyName.toLowerCase().includes(s);
    });
  }, [tab, search, clients, contacts, filters, sortBy, openTaskCountByCompany]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setTab('companies')} className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'companies' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Companies</button>
          <button onClick={() => setTab('contacts')} className={`px-3 py-1.5 rounded-lg text-sm ${tab === 'contacts' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Contacts</button>
          {tab === 'companies' ? (
            <button onClick={() => setShowAddCompanyModal(true)} className="px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600">+ New Company</button>
          ) : (
            <button onClick={() => setShowAddContactModal(true)} className="px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600">+ New Contact</button>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === 'companies' ? 'Search companies...' : 'Search contacts...'}
              className="w-full pl-9 pr-3 py-2 border rounded-lg"
            />
          </div>

          {tab === 'companies' && (
            <>
              <label className="text-sm flex items-center gap-2">
                <input type="checkbox" checked={filters.includes('openTasks')} onChange={() => toggleFilter('openTasks')} />
                Open Tasks
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border rounded-lg px-2 py-1.5"
              >
                <option value="alphabetical">Sort: Alphabetical</option>
                <option value="created_desc">Sort: Newest Created</option>
                <option value="open_tasks">Sort: Open Task Count</option>
              </select>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="text-sm border rounded-lg px-2 py-1.5"
              >
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {tab === 'companies' ? (
          <div className="divide-y">
            {pagedRows.map(company => {
              const companyContacts = contactsByCompany.get(company.id) || [];
              const primaryContact = companyContacts.find(c => c.is_primary) || companyContacts[0];
              const openTaskCount = openTaskCountByCompany.get(company.id) || 0;

              return (
                <div
                  key={company.id}
                  onClick={() => onViewProjects(company)}
                  className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <div className="font-semibold text-gray-900">{company.name}</div>
                    <div className="text-sm text-gray-600">
                      Primary Contact: {primaryContact?.name || 'Unassigned'}
                      {primaryContact?.email ? ` • ${primaryContact.email}` : ''}
                      {' • '}Open Tasks: {openTaskCount}
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <button onClick={(e) => { e.stopPropagation(); onViewProjects(company); }} className="px-3 py-1.5 text-white text-sm rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 shadow-sm hover:opacity-95">View Projects</button>
                    <button onClick={(e) => { e.stopPropagation(); onViewTasks(company); }} className="px-3 py-1.5 text-white text-sm rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 shadow-sm hover:opacity-95">View Tasks</button>
                    <button onClick={(e) => { e.stopPropagation(); onOpenSettings(company); }} className="px-3 py-1.5 text-white text-sm rounded-lg bg-gradient-to-r from-pink-500 to-pink-600 shadow-sm hover:opacity-95">Settings</button>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 && <div className="p-8 text-center text-gray-500">No companies match your filters.</div>}
          </div>
        ) : (
          <div className="divide-y">
            {pagedRows.map(contact => {
              const companyName = (clients || []).find(c => c.id === contact.company_id)?.name || 'Unknown company';
              const company = (clients || []).find(c => c.id === contact.company_id);
              return (
                <div
                  key={contact.id}
                  onClick={() => openContactProfile(contact)}
                  className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <div className="font-semibold text-gray-900">{contact.name || 'Unnamed Contact'}</div>
                    <div className="text-sm text-gray-600">{companyName} • {contact.email || contact.phone || 'No contact info'}</div>
                  </div>

                  <div className="inline-flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); openContactProfile(contact); }}
                      className="px-3 py-1.5 text-white text-sm rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 shadow-sm hover:opacity-95"
                    >
                      Open Profile
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); if (company) onViewProjects(company); }}
                      className="px-3 py-1.5 text-white text-sm rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 shadow-sm hover:opacity-95"
                    >
                      Open Company
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openContactProfile(contact); }}
                      className="px-3 py-1.5 text-white text-sm rounded-lg bg-gradient-to-r from-pink-500 to-pink-600 shadow-sm hover:opacity-95"
                    >
                      Settings
                    </button>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 && <div className="p-8 text-center text-gray-500">No contacts match your search.</div>}
          </div>
        )}

        {showAddCompanyModal && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl max-h-[90vh] overflow-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold">New Company</h3>
                <button className="text-sm text-gray-600" onClick={() => setShowAddCompanyModal(false)}>Close</button>
              </div>
              <div className="p-4 space-y-3">
                <input className="border rounded px-3 py-2 w-full" value={newCompanyForm.name} onChange={(e)=>setNewCompanyForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Company Name" />
                <div className="border rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Contacts (at least one required)</h4>
                    <button onClick={addCompanyContactRow} className="text-sm px-2 py-1 rounded bg-gray-100">+ Add Contact</button>
                  </div>
                  <div className="space-y-2">
                    {newCompanyForm.contacts.map((c, idx) => (
                      <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
                        <input className="border rounded px-2 py-1.5 md:col-span-2" value={c.name || ''} onChange={(e)=>updateCompanyContactRow(idx, 'name', e.target.value)} placeholder="Contact Name" />
                        <input className="border rounded px-2 py-1.5" value={c.email || ''} onChange={(e)=>updateCompanyContactRow(idx, 'email', e.target.value)} placeholder="Email" />
                        <input className="border rounded px-2 py-1.5" value={c.phone || ''} onChange={(e)=>updateCompanyContactRow(idx, 'phone', e.target.value)} placeholder="Phone" />
                        <input className="border rounded px-2 py-1.5" value={c.role || ''} onChange={(e)=>updateCompanyContactRow(idx, 'role', e.target.value)} placeholder="Role" />
                        <div className="flex items-center gap-2">
                          <label className="text-xs flex items-center gap-1"><input type="radio" name="new-company-primary" checked={!!c.is_primary} onChange={() => setNewCompanyForm(prev => ({ ...prev, contacts: prev.contacts.map((row, i) => ({ ...row, is_primary: i === idx })) }))} />Primary</label>
                          <button disabled={newCompanyForm.contacts.length <= 1} onClick={() => removeCompanyContactRow(idx)} className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 disabled:opacity-50">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowAddCompanyModal(false)} className="px-3 py-2 rounded border text-sm">Cancel</button>
                  <button onClick={createCompany} disabled={savingNewRecord} className="px-3 py-2 rounded text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600 disabled:opacity-50">{savingNewRecord ? 'Creating...' : 'Create Company'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showAddContactModal && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-xl rounded-xl shadow-2xl max-h-[90vh] overflow-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold">New Contact</h3>
                <button className="text-sm text-gray-600" onClick={() => setShowAddContactModal(false)}>Close</button>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <select className="border rounded px-3 py-2 md:col-span-2" value={newContactForm.company_id} onChange={(e)=>setNewContactForm(prev => ({ ...prev, company_id: e.target.value }))}>
                  <option value="">Select company...</option>
                  {(clients || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input className="border rounded px-3 py-2" value={newContactForm.name} onChange={(e)=>setNewContactForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Name" />
                <input className="border rounded px-3 py-2" value={newContactForm.email} onChange={(e)=>setNewContactForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Email" />
                <input className="border rounded px-3 py-2" value={newContactForm.phone} onChange={(e)=>setNewContactForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="Phone" />
                <input className="border rounded px-3 py-2" value={newContactForm.role} onChange={(e)=>setNewContactForm(prev => ({ ...prev, role: e.target.value }))} placeholder="Role" />
                <input className="border rounded px-3 py-2" value={newContactForm.title} onChange={(e)=>setNewContactForm(prev => ({ ...prev, title: e.target.value }))} placeholder="Title" />
                <input className="border rounded px-3 py-2" value={newContactForm.department} onChange={(e)=>setNewContactForm(prev => ({ ...prev, department: e.target.value }))} placeholder="Department" />
                <label className="text-sm flex items-center gap-2 md:col-span-2"><input type="checkbox" checked={!!newContactForm.is_primary} onChange={(e)=>setNewContactForm(prev => ({ ...prev, is_primary: e.target.checked }))} />Set as primary contact</label>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button onClick={() => setShowAddContactModal(false)} className="px-3 py-2 rounded border text-sm">Cancel</button>
                  <button onClick={createContact} disabled={savingNewRecord} className="px-3 py-2 rounded text-sm text-white bg-gradient-to-r from-purple-600 to-pink-600 disabled:opacity-50">{savingNewRecord ? 'Creating...' : 'Create Contact'}</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedContact && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl max-h-[90vh] overflow-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold">Contact Profile</h3>
                <button className="text-sm text-gray-600" onClick={() => { setSelectedContact(null); setDeleteConfirmText(''); }}>Close</button>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <input className="border rounded px-3 py-2" value={contactForm.name} onChange={(e)=>setContactForm({...contactForm,name:e.target.value})} placeholder="Name" />
                  <input className="border rounded px-3 py-2" value={contactForm.email} onChange={(e)=>setContactForm({...contactForm,email:e.target.value})} placeholder="Email" />
                  <input className="border rounded px-3 py-2" value={contactForm.phone} onChange={(e)=>setContactForm({...contactForm,phone:e.target.value})} placeholder="Phone" />
                  <input className="border rounded px-3 py-2" value={contactForm.role} onChange={(e)=>setContactForm({...contactForm,role:e.target.value})} placeholder="Role" />
                  <input className="border rounded px-3 py-2" value={contactForm.title} onChange={(e)=>setContactForm({...contactForm,title:e.target.value})} placeholder="Title" />
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <button onClick={saveContactProfile} disabled={profileSaving} className="px-3 py-2 rounded bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm">{profileSaving ? 'Saving...' : 'Save Contact'}</button>
                </div>

                <div className="border rounded p-3">
                  <h4 className="font-medium mb-2">Associated Companies</h4>
                  <div className="space-y-2 mb-3">
                    {contactCompanies.map(cc => (
                      <div key={cc.company_id} className="flex items-center justify-between text-sm">
                        <span>{cc.company_name}{cc.is_primary ? ' (Primary)' : ''}</span>
                        <button onClick={() => removeAssociation(cc.company_id)} className="text-red-600">Remove</button>
                      </div>
                    ))}
                    {contactCompanies.length === 0 && <div className="text-sm text-gray-500">No associations yet.</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={associationCompanyId} onChange={(e)=>setAssociationCompanyId(e.target.value)} className="border rounded px-2 py-1 text-sm">
                      <option value="">Add company...</option>
                      {(clients || []).filter(c => !contactCompanies.find(cc => cc.company_id === c.id)).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button onClick={addAssociation} className="px-2 py-1 text-sm rounded bg-gray-100">Add</button>
                  </div>
                </div>

                <div className="border border-red-200 bg-red-50 rounded p-3 mt-4">
                  <h4 className="font-medium text-red-700 mb-2">Delete Contact</h4>
                  <p className="text-sm text-red-700 mb-2">Type <strong>DELETE</strong> to confirm permanent removal.</p>
                  <div className="flex items-center gap-2">
                    <input
                      className="border border-red-200 rounded px-3 py-2 text-sm flex-1"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                    />
                    <button
                      onClick={deleteContactProfile}
                      disabled={deletingContact || String(deleteConfirmText || '').trim().toLowerCase() !== 'delete'}
                      className="px-3 py-2 rounded bg-red-600 text-white text-sm disabled:opacity-50"
                    >
                      {deletingContact ? 'Deleting...' : 'Delete Contact'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="border-t px-4 py-3 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            Showing {rows.length === 0 ? 0 : (safePage - 1) * pageSize + 1} - {Math.min(safePage * pageSize, rows.length)} of {rows.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="px-3 py-1.5 rounded border text-sm disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-sm text-gray-700">Page {safePage} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="px-3 py-1.5 rounded border text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Client detail component
const ClientDetail = ({ client, projects, onNewProject, onSelectProject, onEditClient, onOpenTask, user, onClientUpdate, badgeCounts = {}, markProjectViewed, currentOrganization }) => {
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('projects');
  const [activityTab, setActivityTab] = useState('communications');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNotesTab, setShowNotesTab] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const { setActiveContext } = useChatContext();

  // Open attachment preview
  const openAttachmentPreview = (attachment) => {
    console.log('≡ƒöì Opening attachment preview:', attachment);
    setPreviewAttachment(attachment);
    setShowPreview(true);
  };

  // Close attachment preview
  const closeAttachmentPreview = () => {
    setShowPreview(false);
    setPreviewAttachment(null);
  };

  // Filter projects based on search query - with defensive array check
  const filteredProjects = (Array.isArray(projects) ? projects : []).filter(project =>
    project?.name?.toLowerCase().includes(projectSearchQuery.toLowerCase()) ||
    (project?.description && project.description.toLowerCase().includes(projectSearchQuery.toLowerCase()))
  );

  

  const tabs = [
    { id: 'projects', label: 'Projects', icon: 'CheckSquare' },
    { id: 'activity', label: 'Activity', icon: 'Activity' },
    { id: 'notes', label: 'Notes', icon: 'BookOpen' },
    { id: 'settings', label: 'Settings', icon: 'Settings' }
  ];

  const handleCreateTask = () => {
    setShowTaskModal(true);
  };

  const handleCreateProject = () => {
    onNewProject();
  };

  const handleCreateNote = () => {
    setActiveTab('notes');
  };

  const handleScheduleMeeting = () => {
    // TODO: Implement meeting scheduling integration
    alert('Meeting scheduling feature coming soon!');
  };

  const handleViewClientPortal = async (client) => {
    try {
      const response = await api.get(`/admin/portal/login-as/${client.id}`);
      if (!response.data?.success) {
        alert(response.data?.error || 'Unable to open portal preview.');
        return;
      }

      const portalData = {
        token: response.data.token,
        contact: response.data.contact,
        client: response.data.client,
        team_id: response.data.team_id
      };
      const encodedData = btoa(JSON.stringify(portalData));
      const portalUrl = `${window.location.origin}/portal/${response.data.team_id}/dashboard#auth=${encodedData}`;
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening client portal preview:', error);
      alert('Failed to open portal preview. Please try again.');
    }
  };

  const handleTaskSave = async (taskData) => {
    // This will use the existing task creation logic
    try {
      const response = await api.post(`/clients/${client.id}/tasks`, taskData);
      
      setShowTaskModal(false);
      // Optionally refresh data or show success message
      alert('Task created successfully!');
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task. Please try again.');
    }
  };

  useEffect(() => {
    if (client?.id) {
      setActiveContext({ view: 'client', client_id: client.id, project_id: null });
    } else {
      setActiveContext({ view: 'client', client_id: null, project_id: null });
    }
  }, [client?.id, setActiveContext]);

  return (
    <div className="flex-1 p-6 bg-gray-50 min-h-screen relative">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Compact Client Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">
                  {client?.name?.charAt(0) || '?'}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{client?.name || 'Unknown Client'}</h1>
                {client?.company && (
                  <p className="text-gray-600">{client.company}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => onEditClient(client)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              Edit Client
            </button>
          </div>
          
          {/* Compact Contact Info */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-2">Contact Information</h3>
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              {client.email && (
                <span><span className="font-medium">Email:</span> {client.email}</span>
              )}
              {client.phone && (
                <span><span className="font-medium">Phone:</span> {client.phone}</span>
              )}
            </div>
            {client.notes && (
              <p className="text-sm text-gray-600 mt-2">{client.notes}</p>
            )}
          </div>
        </div>

        {/* Main Content with Full Width Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col min-h-[600px]">
          {/* Tab Navigation with Action Buttons */}
          <div className="border-b border-gray-200">
            <div className="flex items-center justify-between px-6">
              {/* Tab Navigation */}
              <nav className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-semibold text-base transition-colors ${
                      activeTab === tab.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.icon === 'CheckSquare' && <CheckCircle className="w-5 h-5" />}
                    {tab.icon === 'Activity' && <Activity className="w-5 h-5" />}
                    {tab.icon === 'BookOpen' && <BookOpen className="w-5 h-5" />}
                    {tab.icon === 'Settings' && <SettingsIcon className="w-5 h-5" />}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3 py-2">
                <button
                  onClick={handleCreateTask}
                  className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
                  title="Create Task"
                >
                  <Plus className="w-4 h-4" />
                  <span>Task</span>
                </button>
                
                <button
                  onClick={handleCreateProject}
                  className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
                  title="Create Project"
                >
                  <FolderOpen className="w-4 h-4" />
                  <span>Project</span>
                </button>
                
                <button
                  onClick={handleCreateNote}
                  className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-green-600 to-teal-600 text-white text-sm rounded-lg hover:from-green-700 hover:to-teal-700 transition-all"
                  title="Create Note"
                >
                  <FileText className="w-4 h-4" />
                  <span>Note</span>
                </button>
                
                <button
                  onClick={handleScheduleMeeting}
                  className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white text-sm rounded-lg hover:from-orange-700 hover:to-red-700 transition-all"
                  title="Schedule Meeting"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Meeting</span>
                </button>
                
                <button
                  onClick={() => handleViewClientPortal(client)}
                  className="flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all"
                  title="View Client Portal"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Portal</span>
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 p-6">
            {activeTab === 'projects' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Projects</h2>
                </div>
                
                {/* Project Search Bar */}
                {projects.length > 0 && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search projects..."
                      value={projectSearchQuery}
                      onChange={(e) => setProjectSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map(project => (
                      <div 
                        key={project.id} 
                        className="p-5 border border-gray-200 rounded-lg hover:border-purple-300 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => {
                          markProjectViewed && markProjectViewed(project.id);
                          onSelectProject(project);
                        }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
                              <Badge count={badgeCounts.projects?.[project.id]} />
                            </div>
                            {project.description && (
                              <p className="text-gray-600 text-sm line-clamp-2">{project.description}</p>
                            )}
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            project.status === 'completed' ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200' :
                            project.status === 'in-progress' ? 'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200' :
                            'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200'
                          }`}>
                            {project.status ? (project.status.charAt(0).toUpperCase() + project.status.slice(1)) : 'Open'}
                          </span>
                        </div>
                        <div className="flex justify-end">
                          <button className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors">
                            Open Project
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-12">
                      <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Projects</h3>
                      <p className="text-gray-600 mb-4">
                        Get started by creating your first project for {client.name}.
                      </p>
                      <button
                        onClick={onNewProject}
                        className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        Create First Project
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <ClientActivityFeed 
                client={client} 
                user={user}
                onSelectProject={onSelectProject}
                onOpenTask={onOpenTask}
                  />
            )}

            {activeTab === 'notes' && (
              <NotesTab 
                client={client} 
                projects={projects} 
                user={user} 
              />
            )}

            {activeTab === 'settings' && (
              <ClientSettings
                client={client}
                user={user}
                onClose={() => {}} // Close not needed since this is embedded
                onClientUpdate={onClientUpdate}
              />
            )}
          </div>
        </div>
      </div>

      {/* Task Modal for creating client tasks */}
      {showTaskModal && (
        <TaskModal
          isOpen={showTaskModal}
          onClose={() => setShowTaskModal(false)}
          onSave={handleTaskSave}
          user={user}
          clientId={client.id}
          projectId={null} // No specific project, this is a client-level task
          existingTask={null}
          openAttachmentPreview={openAttachmentPreview}
          onDelete={async (taskId) => {
            try {
              await api.delete(`/tasks/${taskId}`);
              setShowTaskModal(false);
              // Refresh client data
              fetchClient();
            } catch (error) {
              console.error('Failed to delete task:', error);
              alert(`Failed to delete task: ${error.response?.data?.detail || error.message}`);
            }
          }}
          onArchive={async (taskId, currentlyArchived) => {
            try {
              await api.put(`/tasks/${taskId}/archive`, { archived: !currentlyArchived });
              // Refresh client data
              fetchClient();
            } catch (error) {
              console.error('Failed to archive task:', error);
              alert(`Failed to ${currentlyArchived ? 'unarchive' : 'archive'} task: ${error.response?.data?.detail || error.message}`);
            }
          }}
        />
      )}

      {/* Attachment Preview Modal */}
      <AttachmentPreview
        attachment={previewAttachment}
        isOpen={showPreview}
        onClose={closeAttachmentPreview}
      />
    </div>
  );
};

const AppFrame = () => {
  const location = useLocation();
  const isMessagingRoute = location.pathname === '/organization/messaging';

  return (
    <div className={`flex flex-col ${isMessagingRoute ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
      <div className={`app-content flex flex-col min-h-0 ${isMessagingRoute ? 'app-content--messaging' : ''}`}>
        <Routes>
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/pricing" element={<PricingSignup />} />
            <Route path="/signup/success" element={<SignupSuccess />} />
            <Route path="/reactivate" element={<ReactivateAccount />} />
            
            {/* NEW PORTAL ROUTES (Redesigned System) */}
            <Route path="/portal/:team_id" element={<NewPortalLogin />} />
            <Route path="/portal/:team_id/verify-email" element={<PortalEmailVerification />} />
            <Route path="/portal/:team_id/set-password" element={<PortalPasswordSetup />} />
            <Route path="/portal/:team_id/resend-verification" element={<PortalResendVerification />} />
            <Route path="/portal/:team_id/forgot-password" element={<PortalForgotPassword />} />
            <Route path="/portal/:team_id/reset-password" element={<PortalResetPassword />} />
            <Route path="/portal/:team_id/select-client" element={<PortalSelectClient />} />
            <Route path="/portal/:team_id/dashboard" element={<NewPortalDashboard />} />
            
            {/* OLD PORTAL ROUTES (Legacy - To Be Removed) */}
            <Route path="/client" element={<LegacyPortalRedirect />} />
            <Route path="/client/:clientId" element={<LegacyPortalRedirect />} />
            <Route path="/portal/login" element={<LegacyPortalRedirect />} />
            <Route path="/portal/forgot-password" element={<LegacyPortalRedirect />} />
            <Route path="/portal/reset-password" element={<LegacyPortalRedirect />} />
            
            <Route path="/test" element={<TestComponent />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/support" element={<Support />} />
            <Route path="/zoom-documentation" element={<ZoomDocumentation />} />
            <Route path="/super-admin" element={<SuperAdminApp />} />
            
            {/* Main App Routes with URL-based navigation */}
            <Route path="/schedule" element={<MainApp initialView="schedule" />} />
            <Route path="/inbox" element={<MainApp initialView="inbox" />} />
            <Route path="/tasks" element={<MainApp initialView="task-hub" />} />
            <Route path="/tasks/:taskId" element={<MainApp initialView="task-hub" />} />
            <Route path="/settings" element={<MainApp initialView="settings" />} />
            <Route path="/settings/:section" element={<MainApp initialView="settings" />} />
            
            {/* Organization/Company views */}
            <Route path="/organization" element={<MainApp initialView="organization" initialOrgView="projects" />} />
            <Route path="/organization/projects" element={<MainApp initialView="organization" initialOrgView="projects" />} />
            <Route path="/organization/companies" element={<MainApp initialView="organization" initialOrgView="companies" />} />
            <Route path="/organization/contacts" element={<MainApp initialView="organization" initialOrgView="contacts" />} />
            <Route path="/organization/personal" element={<MainApp initialView="organization" initialOrgView="personal" />} />
            <Route path="/organization/messaging" element={<MainApp initialView="organization" initialOrgView="messaging" />} />
            
            {/* Client routes */}
            <Route path="/clients" element={<MainApp initialView="organization" initialOrgView="projects" />} />
            <Route path="/clients/:clientId/tasks" element={<MainApp initialView="client-tasks" />} />
            <Route path="/clients/:clientId" element={<MainApp initialView="client" />} />
            <Route path="/clients/:clientId/projects/:projectId" element={<MainApp initialView="project" />} />
            
            {/* Governance */}
            <Route path="/governance" element={<MainApp initialView="governance" />} />
            <Route path="/governance/documents" element={<MainApp initialView="governance" />} />
            <Route path="/governance/documents/:documentId" element={<DocumentViewer />} />
            <Route path="/governance/meetings/:meetingId" element={<LiveMeetingRunner />} />
            <Route path="/governance/settings" element={<BoardSettings />} />
            <Route path="/governance/committees/:committeeId" element={<CommitteeDashboard />} />
            <Route path="/governance/committees/:committeeId/settings" element={<CommitteeSettings />} />
            <Route path="/governance/committees/:committeeId/meetings/:meetingId/*" element={<CommitteeMeetingRunner />} caseSensitive={false} />
            
            {/* Direct project access */}
            <Route path="/projects/:projectId" element={<MainApp initialView="project" />} />
            
            {/* Dashboard (default) */}
            <Route path="/" element={<MainApp initialView="dashboard" />} />
            <Route path="*" element={<MainApp />} />
          </Routes>
      </div>
      <GlobalChatWidget />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ChatContextProvider>
        <AppFrame />
      </ChatContextProvider>
    </BrowserRouter>
  );
}

// Main application component (extracted from original App)
function MainApp({ initialView = 'dashboard', initialOrgView = null }) {
  const { user, loading, login, register, loginWithGoogle, logout, updateUser, completeMFALogin } = useAuth();
  const { setActiveContext, setAuthContext } = useChatContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId: urlClientId, projectId: urlProjectId, taskId: urlTaskId, section: urlSection } = useParams();
  
  // Use URL-based view from props (from router) as the source of truth
  const [currentView, setCurrentView] = useState(initialView);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [organizationName, setOrganizationName] = useState('My Organization');
  const [organizationView, setOrganizationView] = useState(initialOrgView);
  
  // Sync currentView with initialView prop when route changes
  useEffect(() => {
    setCurrentView(initialView);
    if (initialOrgView !== null) {
      setOrganizationView(initialOrgView);
    }
  }, [initialView, initialOrgView]);

  // Prevent lingering profile modal when navigating into Settings
  useEffect(() => {
    if (currentView === 'settings') {
      setShowUserProfile(false);
      setShowTaskModal(false);
      setShowQuickTaskModal(false);
      setShowImportWizard(false);
      setShowPreview(false);
      setShowKeyboardHelp(false);
    }
  }, [currentView]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedProjectForTask, setSelectedProjectForTask] = useState(null);
  // Track where to return after closing task modal (used for non-sessionStorage cases)
  const [taskModalReturnPath, setTaskModalReturnPath] = useState(null);
  // Track whether task modal should keep URL in /tasks/:id mode.
  const [taskModalAllowUrlSync, setTaskModalAllowUrlSync] = useState(true);
  const [taskHubRefreshTrigger, setTaskHubRefreshTrigger] = useState(0);
  const [organizationRefreshTrigger, setOrganizationRefreshTrigger] = useState(0);
  const [generalProject, setGeneralProject] = useState(null); // Team's general project for tasks without specific project
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showClientSettings, setShowClientSettings] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState({ total: 0, unread: 0 });
  const [badgeCounts, setBadgeCounts] = useState({
    notifications: 0,
    internal_messages: 0,
    clients: {},
    projects: {}
  });
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [crmTasks, setCrmTasks] = useState([]);
  const [userSettings, setUserSettings] = useState({ default_client_type: 'business' });
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [hasRestoredState, setHasRestoredState] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  useEffect(() => {
    const resolvedTeamId = currentOrganization?.id || user?.current_team_id || user?.team_id || null;
    setAuthContext({ teamId: resolvedTeamId, userId: user?.id || null });
  }, [currentOrganization?.id, user?.current_team_id, user?.team_id, user?.id, setAuthContext]);

  useEffect(() => {
    if (initialView === 'dashboard') {
      setActiveContext({ view: 'dashboard', client_id: null, project_id: null });
    } else if (initialView === 'task-hub') {
      setActiveContext({ view: 'tasks', client_id: null, project_id: null });
    }
  }, [initialView, setActiveContext]);
  
  // Quick task modal state
  const [showQuickTaskModal, setShowQuickTaskModal] = useState(false);
  const [quickTaskPrefilledData, setQuickTaskPrefilledData] = useState({});
  
  // Keyboard shortcuts help modal state
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  
  // Subscription access state
  const [subscriptionAccess, setSubscriptionAccess] = useState({ has_access: true });
  const [showGracePeriodBanner, setShowGracePeriodBanner] = useState(true);

  // Governance access state
  const [governanceAccess, setGovernanceAccess] = useState({
    show_governance_nav: false,
    board_enabled: false,
    committees_enabled: false,
    has_board_access: false,
    committees: []
  });

  // Fetch governance access
  useEffect(() => {
    const fetchGovernanceAccess = async () => {
      if (!user) return;
      try {
        setGovernanceAccess({
          show_governance_nav: false,
          board_enabled: false,
          committees_enabled: false,
          has_board_access: false,
          committees: []
        });
        const response = await api.get('/governance/access');
        setGovernanceAccess(response.data);
      } catch (error) {
        console.error('Error fetching governance access:', error);
      }
    };
    fetchGovernanceAccess();
  }, [user, currentOrganization?.id]);

  // Check subscription access
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) return;
      
      try {
        const response = await api.get('/billing/access-check');
        
        if (response.data.success) {
          setSubscriptionAccess(response.data);
          
          // Redirect to reactivate page if locked
          if (!response.data.has_access && response.data.status === 'locked') {
            window.location.href = '/reactivate';
          }
        }
      } catch (error) {
        console.error('Error checking subscription access:', error);
        // On error, allow access (graceful degradation)
      }
    };
    
    checkAccess();
    // Re-check every 5 minutes
    const interval = setInterval(checkAccess, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // URL-based navigation: Load client/project from URL params
  useEffect(() => {
    const loadFromUrlParams = async () => {
      if (!user) return;
      
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      try {
        // If we have a clientId from URL, load that client
        if (urlClientId) {
          console.log('≡ƒöù Loading client from URL:', urlClientId);
          const clientResponse = await api.get(`/clients/${urlClientId}`);
          setSelectedClient(clientResponse.data);
          
          // Also fetch the client's projects
          const projectsResponse = await api.get(`/clients/${urlClientId}/projects`);
          setProjects(projectsResponse.data);
          
          // If we also have a projectId, load that project
          if (urlProjectId) {
            console.log('≡ƒöù Loading project from URL:', urlProjectId);
            const projectResponse = await api.get(`/projects/${urlProjectId}`);
            setSelectedProject(projectResponse.data);
          }
        }
        // If we have a projectId but no clientId (direct project access)
        else if (urlProjectId && !urlClientId) {
          console.log('≡ƒöù Loading project directly from URL:', urlProjectId);
          const projectResponse = await api.get(`/projects/${urlProjectId}`);
          setSelectedProject(projectResponse.data);
          
          // If project has a client_id, load that client too
          if (projectResponse.data.client_id) {
            const clientResponse = await api.get(`/clients/${projectResponse.data.client_id}`);
            setSelectedClient(clientResponse.data);
          }
        }
      } catch (error) {
        console.error('Γ¥î Failed to load from URL params:', error);
        // If loading fails, navigate to dashboard
        navigate('/');
      }
    };
    
    loadFromUrlParams();
  }, [user, urlClientId, urlProjectId, navigate]);

  // Open attachment preview
  const openAttachmentPreview = (attachment) => {
    console.log('≡ƒöì MainApp - Opening attachment preview:', attachment);
    setPreviewAttachment(attachment);
    setShowPreview(true);
  };

  // Close attachment preview
  const closeAttachmentPreview = () => {
    setShowPreview(false);
    setPreviewAttachment(null);
  };

  // Keyboard shortcuts - must be before any conditional returns
  useKeyboardShortcuts({
    newTask: () => {
      if (user) {
        setShowQuickTaskModal(true);
      }
    },
    quickSearch: () => {
      // Focus the search input if it exists
      const searchInput = document.querySelector('input[placeholder*="Search"]');
      if (searchInput) {
        searchInput.focus();
      }
    },
    showHelp: () => {
      setShowKeyboardHelp(true);
    },
    closeModal: () => {
      if (showKeyboardHelp) {
        setShowKeyboardHelp(false);
      } else if (showTaskModal) {
        setShowTaskModal(false);
        setEditingTask(null);
      } else if (showQuickTaskModal) {
        setShowQuickTaskModal(false);
      }
    },
    goToDashboard: () => {
      if (user) {
        setSelectedClient(null);
        setSelectedProject(null);
        navigate('/');
        setCurrentView('dashboard');
      }
    },
    goToTaskHub: () => {
      if (user) {
        navigate('/tasks');
        setCurrentView('task-hub');
      }
    },
    saveTask: () => {
      // Trigger save - dispatch event for TaskModal to handle
      window.dispatchEvent(new CustomEvent('saveTask'));
    },
    saveAndClose: () => {
      // Trigger save and close - dispatch event for TaskModal to handle
      window.dispatchEvent(new CustomEvent('saveAndCloseTask'));
    },
    markComplete: () => {
      // Trigger mark complete - dispatch event for TaskModal to handle
      window.dispatchEvent(new CustomEvent('markTaskComplete'));
    },
    moveTaskLeft: () => {
      // Trigger move task left - dispatch event for Kanban to handle
      window.dispatchEvent(new CustomEvent('moveTaskLeft'));
    },
    moveTaskRight: () => {
      // Trigger move task right - dispatch event for Kanban to handle
      window.dispatchEvent(new CustomEvent('moveTaskRight'));
    }
  }, { enabled: !!user });

  // Handle task_id query parameter from email links
  useEffect(() => {
    // Check sessionStorage first (saved before OAuth cleanup)
    const pendingTaskId = sessionStorage.getItem('pending_task_id');
    const urlParams = new URLSearchParams(window.location.search);
    const taskId = pendingTaskId || urlParams.get('task_id');
    
    if (taskId && user) {
      console.log('≡ƒôº Opening task from email link:', taskId);
      
      // Clear the pending task ID
      sessionStorage.removeItem('pending_task_id');
      
      // Fetch the task to get its details
      const openTaskFromEmail = async () => {
        try {
          const response = await api.get(`/tasks/${taskId}`);
          const task = response.data;
          
          // Set up the task modal with the task data
          setEditingTask(task);
          
          // If task has a project, load it
          if (task.project_id) {
            const projectResponse = await api.get(`/projects/${task.project_id}`);
            setSelectedProjectForTask(projectResponse.data);
          }
          
          // Open the task modal
          setShowTaskModal(true);
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error opening task from email:', error);
          if (error.response?.status === 404) {
            alert('Unable to open task. It may have been deleted or you may not have access.');
          } else {
            alert('Error opening task. Please try again.');
          }
        }
      };
      
      openTaskFromEmail();
    }
  }, [user]);

  // State persistence functions
  const saveNavigationState = useCallback(() => {
    const navigationState = {
      currentView,
      selectedClientId: selectedClient?.id || null,
      selectedProjectId: selectedProject?.id || null,
      organizationView,
      timestamp: new Date().getTime()
    };
    console.log('≡ƒÆ╛ Saving navigation state:', navigationState);
    localStorage.setItem('navigationState', JSON.stringify(navigationState));
  }, [currentView, selectedClient, selectedProject, organizationView]);

  const restoreNavigationState = useCallback(async () => {
    try {
      console.log('≡ƒöä Attempting to restore navigation state...');
      
      // FIRST: Check for notification navigation intent (one-time use)
      const notificationNavStr = sessionStorage.getItem('notificationNav');
      if (notificationNavStr) {
        const notificationNav = JSON.parse(notificationNavStr);
        console.log('≡ƒöö Found notification navigation intent:', notificationNav);
        
        // Clear it immediately to prevent repeated navigation
        sessionStorage.removeItem('notificationNav');
        
        // Only process if it's recent (within last 5 seconds)
        if (Date.now() - notificationNav.timestamp < 5000) {
          const url = notificationNav.url;
          const data = notificationNav.data || {};
          
          // Handle different notification types
          if (url.includes('/organization/messaging')) {
            const conversationId = data.conversation_id || new URLSearchParams(url.split('?')[1] || '').get('conversation');
            if (conversationId) {
              console.log('≡ƒô¿ Opening conversation:', conversationId);
              navigate('/organization/messaging');
              setOrganizationView('messaging');
              // Store for InternalMessaging to pick up
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openConversation', { detail: conversationId }));
              }, 500);
              setHasRestoredState(true);
              return;
            }
          } else if (url.includes('/tasks/')) {
            const taskId = url.split('/tasks/')[1]?.split(/[?#]/)[0] || data.task_id;
            if (taskId) {
              console.log('Γ£à Opening task:', taskId);
              // Store for app to open task modal
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openTask', { detail: taskId }));
              }, 500);
              setHasRestoredState(true);
              return;
            }
          } else if (url.includes('/projects/')) {
            const projectId = url.split('/projects/')[1]?.split(/[?#]/)[0] || data.project_id;
            if (projectId) {
              console.log('≡ƒôü Opening project:', projectId);
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openProject', { detail: projectId }));
              }, 500);
              setHasRestoredState(true);
              return;
            }
          } else if (url.includes('/clients/')) {
            const clientId = url.split('/clients/')[1]?.split(/[?#]/)[0] || data.client_id;
            if (clientId) {
              console.log('≡ƒæñ Opening client:', clientId);
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('openClient', { detail: clientId }));
              }, 500);
              setHasRestoredState(true);
              return;
            }
          }
        } else {
          console.log('ΓÅ░ Notification navigation expired, ignoring');
        }
      }
      
      // No notification navigation - proceed with normal state restoration
      // With URL-based routing, we only restore state if user lands on root '/'
      // and we have saved state to restore to the correct URL
      const isAtRoot = location.pathname === '/';
      if (!isAtRoot) {
        console.log('≡ƒöù URL already specifies destination, skipping localStorage restoration');
        setHasRestoredState(true);
        return;
      }
      
      console.log('≡ƒöì Current localStorage navigationState:', localStorage.getItem('navigationState'));
      const savedState = localStorage.getItem('navigationState');
      if (!savedState) {
        console.log('Γ¥î No saved navigation state found');
        setHasRestoredState(true);
        return;
      }

      const navigationState = JSON.parse(savedState);
      console.log('≡ƒôû Parsed saved state:', navigationState);
      
      // Check if state is less than 24 hours old (optional - prevents stale state)
      const ONE_DAY = 24 * 60 * 60 * 1000;
      if (new Date().getTime() - navigationState.timestamp > ONE_DAY) {
        console.log('ΓÅ░ Saved state expired (>24 hours), clearing...');
        localStorage.removeItem('navigationState');
        setHasRestoredState(true);
        return;
      }

      // Navigate to the correct URL based on saved state
      if (navigationState.selectedProjectId && navigationState.selectedClientId) {
        console.log('≡ƒöù Restoring to project view');
        navigate(`/clients/${navigationState.selectedClientId}/projects/${navigationState.selectedProjectId}`);
      } else if (navigationState.selectedClientId) {
        console.log('≡ƒöù Restoring to client view');
        navigate(`/clients/${navigationState.selectedClientId}`);
      } else if (navigationState.currentView === 'organization' && navigationState.organizationView) {
        console.log('≡ƒöù Restoring to organization view');
        navigate(`/organization/${navigationState.organizationView}`);
      } else if (navigationState.currentView && navigationState.currentView !== 'dashboard') {
        // Map old view names to new URLs
        const viewToUrl = {
          'schedule': '/schedule',
          'inbox': '/inbox',
          'task-hub': '/tasks',
          'settings': '/settings'
        };
        const url = viewToUrl[navigationState.currentView];
        if (url) {
          console.log('≡ƒöù Restoring to view:', navigationState.currentView);
          navigate(url);
        }
      }
      
      console.log('Γ£à Navigation state restoration complete');
    } catch (error) {
      console.error('Γ¥î Error restoring navigation state:', error);
      localStorage.removeItem('navigationState');
    } finally {
      // Mark that we've attempted restoration
      setHasRestoredState(true);
    }
  }, [location.pathname, navigate]);

  // Save navigation state whenever it changes (but only AFTER initial restoration)
  useEffect(() => {
    if (user && hasRestoredState) {
      console.log('≡ƒÆ╛ Saving navigation state (post-restoration)');
      saveNavigationState();
    } else if (user && !hasRestoredState) {
      console.log('ΓÅ╕∩╕Å Skipping save - waiting for restoration');
    }
  }, [currentView, selectedClient, selectedProject, organizationView, user, saveNavigationState, hasRestoredState]);

  // Listen for notification-triggered navigation events
  useEffect(() => {
    const handleOpenTask = async (event) => {
      const taskId = event.detail;
      console.log('≡ƒöö Event: Open task', taskId);
      
      try {
        const response = await api.get(`/tasks/${taskId}`);
        
        // Dispatch openTaskModal event instead of calling handleTaskClick directly
        window.dispatchEvent(new CustomEvent('openTaskModal', { detail: response.data }));
      } catch (error) {
        console.error('Failed to fetch and open task:', error);
      }
    };
    
    window.addEventListener('openTask', handleOpenTask);
    return () => {
      window.removeEventListener('openTask', handleOpenTask);
    };
  }, []);

  // Import Wizard handlers
  const handleShowImportWizard = () => {
    setShowImportWizard(true);
  };

  const handleImportWizardComplete = () => {
    // Refresh client data and dashboard after import
    fetchClients();
    fetchDashboardStats();
    console.log('Import wizard completed - refreshing data');
  };

  // Memoized callback functions to prevent unnecessary re-renders
  const handleBackToDashboard = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Fetch data functions
  const fetchCurrentOrganization = async () => {
    try {
      const response = await api.get('/user/current-organization');
      setCurrentOrganization(response.data);
      // Don't override organizationName - let fetchOrganizationName handle it with custom variables
    } catch (error) {
      console.error('Failed to fetch current organization:', error);
      // Keep default values if fetch fails
    }
  };

  const handleOrganizationChange = (newOrganization) => {
    setCurrentOrganization(newOrganization);
    // Don't override organizationName - preserve the custom company name variable
    
    // Refresh data for new organization context
    fetchClients();
    fetchContacts();
    fetchCrmTasks();
    fetchProjects();
    
    // Reset current selections since they may not exist in new organization
    setSelectedClient(null);
    setSelectedProject(null);
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      
      // If permission error, try to migrate user
      if (error.response?.status === 403) {
        console.log('Permission denied, attempting user migration...');
        const migrationSuccessful = await migrateUser();
        if (migrationSuccessful) {
          // Retry fetching clients after migration
          try {
            const response = await api.get('/clients');
            setClients(response.data);
            console.log('Clients fetched successfully after migration');
          } catch (retryError) {
            console.error('Still failed to fetch clients after migration:', retryError);
          }
        }
      }
    }
  };

  const fetchContacts = async () => {
    try {
      const firstPage = await api.get(`${API_V1_BASE}/api/v1/contacts`, { params: { limit: 200 } });
      const all = [...(firstPage.data?.data || [])];
      let nextCursor = firstPage.data?.meta?.next_cursor;

      while (nextCursor) {
        const page = await api.get(`${API_V1_BASE}/api/v1/contacts`, { params: { limit: 200, cursor: nextCursor } });
        all.push(...(page.data?.data || []));
        nextCursor = page.data?.meta?.next_cursor;
      }

      const companyMap = new Map((clients || []).map(c => [c.id, c.name]));
      const withCompanyNames = all.map(contact => ({
        ...contact,
        company_name: companyMap.get(contact.company_id) || contact.company_name || null,
      }));

      setContacts(withCompanyNames);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      setContacts([]);
    }
  };

  const fetchCrmTasks = async () => {
    try {
      const response = await api.get('/tasks/hub', {
        params: {
          include_completed: true,
          include_subtasks: true,
          sort_by: 'due_date'
        }
      });
      const items = Array.isArray(response.data?.tasks) ? response.data.tasks : [];
      setCrmTasks(items);
    } catch (error) {
      console.error('Failed to fetch CRM tasks:', error);
      setCrmTasks([]);
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await api.get('/user/settings');
      setUserSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    }
  };

  const fetchProjects = async (clientId) => {
    try {
      if (clientId) {
        const response = await api.get(`/clients/${clientId}/projects`);
        setProjects(response.data);
      } else {
        // Fetch all team projects (no client filter)
        const response = await api.get('/projects');
        setProjects(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/team');
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await api.get('/notifications/count');
      
      // Only update state if the count actually changed to prevent unnecessary re-renders
      setNotificationCount(prevCount => {
        const newCount = response.data;
        if (prevCount === newCount) {
          return prevCount; // Return same value to prevent re-render
        }
        return newCount;
      });
    } catch (error) {
      console.error('Failed to fetch notification count:', error);
    }
  };

  const fetchBadgeCounts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      const response = await api.get('/badges/counts');
      
      setBadgeCounts(prevCounts => {
        // Only update if data actually changed
        if (JSON.stringify(prevCounts) === JSON.stringify(response.data)) {
          return prevCounts;
        }
        return response.data;
      });
    } catch (error) {
      console.error('Failed to fetch badge counts:', error);
    }
  };

  const markProjectViewed = async (projectId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      await api.post(`/projects/${projectId}/mark-viewed`, {});
      
      // Refresh badge counts after marking as viewed
      await fetchBadgeCounts();
    } catch (error) {
      console.error('Failed to mark project as viewed:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('No auth token found for dashboard stats');
        return;
      }
      
      const response = await api.get('/dashboard/stats');
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      if (error.response?.status === 401) {
        console.log('Dashboard stats: Token expired or invalid');
      }
    }
  };

  const fetchUpcomingMeetings = async () => {
    try {
      const response = await api.get('/meetings');
      
      // Filter for upcoming meetings (next 7 days)
      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      const upcoming = response.data.meetings?.filter(meeting => {
        if (!meeting.start_time) return false;
        const meetingDate = new Date(meeting.start_time);
        return meetingDate >= now && meetingDate <= nextWeek;
      }) || [];
      
      setUpcomingMeetings(upcoming);
    } catch (error) {
      console.error('Failed to fetch upcoming meetings:', error);
      setUpcomingMeetings([]); // Set empty array on error
    }
  };

  const fetchOrganizationName = async () => {
    try {
      // First try to get company name from custom values (same as QuickAddTaskModal)
      try {
        const customValuesResponse = await api.get('/custom-values');
        
        const customValues = customValuesResponse.data || [];
        const companyNameValue = customValues.find(v => v.variable_name === 'company_name');
        
        if (companyNameValue && companyNameValue.value) {
          setOrganizationName(companyNameValue.value);
          return; // Exit early if we found the custom company name
        }
      } catch (customValuesError) {
        console.log('Could not fetch custom values, trying team info...');
      }
      
      // Fallback to team info
      const teamResponse = await api.get('/team');
      
      const teamName = teamResponse.data?.team?.name;
      const companyName = teamResponse.data?.team?.company_name;
      
      // Use company name first, then team name, then default
      if (companyName) {
        setOrganizationName(companyName);
      } else if (teamName) {
        setOrganizationName(teamName);  
      } else {
        setOrganizationName('My Organization');
      }
    } catch (teamError) {
      console.log('Could not fetch team info, using default organization name');
      setOrganizationName('My Organization');
    }
  };

  // Load initial data when user is authenticated
  useEffect(() => {
    if (user) {
      fetchCurrentOrganization();
      fetchClients();
      fetchContacts();
      fetchCrmTasks();
      fetchDashboardStats();
      fetchUpcomingMeetings();
      fetchTeamMembers();
      fetchNotificationCount();
      fetchUserSettings();
      fetchOrganizationName();
      fetchGeneralProject(); // Fetch General project (created on team setup)
      
      // Restore previous navigation state after data is loaded
      setTimeout(() => {
        restoreNavigationState();
      }, 500); // Small delay to ensure clients are loaded first
    }
  }, [user, restoreNavigationState]); // Restore [user] dependency so data loads when user is authenticated

  // Set up notification and badge polling separately to avoid constant recreation
  useEffect(() => {
    if (!user) return;
    
    // Fetch immediately on mount
    fetchBadgeCounts();
    
    // Only set up polling if user is authenticated
    const badgeInterval = setInterval(() => {
      // Only poll if user is still authenticated
      if (localStorage.getItem('access_token')) {
        fetchNotificationCount();
        fetchBadgeCounts();
      }
    }, 30000); // Poll every 30 seconds
    
    return () => clearInterval(badgeInterval);
  }, [user?.id]); // Use user.id to avoid recreating interval unnecessarily

  // Setup event listeners
  useEffect(() => {
    // Listen for custom event to open user profile modal
    const handleOpenUserProfile = (event) => {
      setShowUserProfile(true);
      // Store event details for UserProfile component to use
      if (event.detail) {
        window.userProfileEventDetails = event.detail;
      }
    };
    
    // Listen for custom event to refresh badges
    const handleRefreshBadges = () => {
      fetchBadgeCounts();
    };
    
    window.addEventListener('openUserProfile', handleOpenUserProfile);
    window.addEventListener('refreshBadges', handleRefreshBadges);
    
    return () => {
      window.removeEventListener('openUserProfile', handleOpenUserProfile);
      window.removeEventListener('refreshBadges', handleRefreshBadges);
    };
  }, []);

  // Client management
  const handleClientSave = async (clientData) => {
    try {
      console.log('≡ƒôñ Sending client data to backend:', JSON.stringify(clientData, null, 2));
      console.log('≡ƒôç Contacts array:', clientData.contacts);
      console.log('≡ƒôç Contacts count:', clientData.contacts?.length || 0);
      
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, clientData);
      } else {
        await api.post('/clients', clientData);
      }
      
      setShowClientForm(false);
      setEditingClient(null);
      fetchClients();
      fetchContacts();
      if (selectedClient && editingClient?.id === selectedClient.id) {
        // Refresh selected client data
        const updatedClient = { ...selectedClient, ...clientData };
        setSelectedClient(updatedClient);
      }
    } catch (error) {
      console.error('Failed to save client:', error);
      console.error('≡ƒôè Detailed error info:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        validationErrors: error.response?.data?.detail
      });
      alert(`Failed to save client: ${error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || error.message}`);
    }
  };

  const handleClientImport = async (payloads) => {
    if (!Array.isArray(payloads) || payloads.length === 0) {
      alert('No valid rows to import.');
      return;
    }

    try {
      for (const payload of payloads) {
        const response = await api.post('/clients', payload.client);
        const createdClient = response.data;
        const clientId = createdClient?.id;

        if (clientId && payload.custom_fields) {
          const fieldEntries = Object.entries(payload.custom_fields);
          for (const [fieldId, value] of fieldEntries) {
            await api.put(`/clients/${clientId}/custom-fields/${fieldId}`, { value });
          }
        }
      }

      setShowClientForm(false);
      setEditingClient(null);
      fetchClients();
      fetchContacts();
      alert(`Γ£à Imported ${payloads.length} clients successfully.`);
    } catch (error) {
      console.error('Failed to import clients:', error);
      alert(`Failed to import clients: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    fetchProjects(client.id);
    navigate(`/clients/${client.id}`);
  };

  const handleSelectContact = (contact) => {
    if (contact?.company_id) {
      const company = (clients || []).find(c => c.id === contact.company_id);
      if (company) {
        handleSelectClient(company);
      } else {
        navigate(`/clients/${contact.company_id}`);
      }
    }
  };

  // User migration function
  const migrateUser = async () => {
    try {
      console.log('≡ƒöº Attempting user migration...');
      const response = await api.post('/admin/migrate-users', {});
      console.log('Γ£à User migration successful:', response.data);
      return true;
    } catch (error) {
      console.error('Γ¥î User migration failed:', error);
      console.error('≡ƒôè Migration error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      return false;
    }
  };

  const handleClientDelete = async (clientId, clientName) => {
    // Enhanced deletion confirmation requiring typing "DELETE"
    const confirmationDialog = () => {
      return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4';
        modal.innerHTML = `
          <div class="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div class="p-6">
              <div class="flex items-center mb-4">
                <div class="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                </div>
                <div class="ml-4">
                  <h3 class="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
                  <p class="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
              
              <div class="mb-6">
                <p class="text-gray-700 mb-4">
                  You are about to permanently delete <strong>"${clientName}"</strong> and ALL associated:
                </p>
                <ul class="text-sm text-gray-600 list-disc list-inside space-y-1 mb-4">
                  <li>Projects and tasks</li>
                  <li>Notes and documents</li>
                  <li>Client activity history</li>
                  <li>Custom field data</li>
                </ul>
                <div class="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p class="text-sm text-red-800">
                    <strong>Warning:</strong> This will move the client to trash. Admins can recover deleted items within 10 days.
                  </p>
                </div>
                <p class="text-gray-700 mb-3">
                  To confirm, please type <strong class="text-red-600">DELETE</strong> in the box below:
                </p>
                <input 
                  type="text" 
                  id="deleteConfirmation" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Type DELETE to confirm"
                  autocomplete="off"
                />
                <div id="confirmationError" class="text-red-600 text-sm mt-2 hidden">
                  Please type "DELETE" exactly as shown to confirm.
                </div>
              </div>
              
              <div class="flex justify-end space-x-3">
                <button 
                  id="cancelDelete" 
                  class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  id="confirmDelete" 
                  class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled
                >
                  Delete Client
                </button>
              </div>
            </div>
          </div>
        `;
        
        document.body.appendChild(modal);
        
        const input = modal.querySelector('#deleteConfirmation');
        const confirmBtn = modal.querySelector('#confirmDelete');
        const cancelBtn = modal.querySelector('#cancelDelete');
        const errorDiv = modal.querySelector('#confirmationError');
        
        // Enable/disable confirm button based on input
        input.addEventListener('input', () => {
          const isValid = input.value === 'DELETE';
          confirmBtn.disabled = !isValid;
          if (input.value && !isValid) {
            errorDiv.classList.remove('hidden');
          } else {
            errorDiv.classList.add('hidden');
          }
        });
        
        // Handle confirmation
        confirmBtn.addEventListener('click', () => {
          if (input.value === 'DELETE') {
            document.body.removeChild(modal);
            resolve(true);
          }
        });
        
        // Handle cancellation
        cancelBtn.addEventListener('click', () => {
          document.body.removeChild(modal);
          resolve(false);
        });
        
        // Focus the input
        setTimeout(() => input.focus(), 100);
        
        // Handle escape key
        const handleEscape = (e) => {
          if (e.key === 'Escape') {
            document.body.removeChild(modal);
            document.removeEventListener('keydown', handleEscape);
            resolve(false);
          }
        };
        document.addEventListener('keydown', handleEscape);
      });
    };

    const confirmed = await confirmationDialog();
    if (!confirmed) return;
    
    try {
      const response = await api.delete(`/clients/${clientId}`);
      
      // Show success message with recovery information
      const successMessage = `
        Γ£à Client "${clientName}" has been moved to trash successfully.
        
        ≡ƒÆí Recovery: Admins can recover deleted items from Settings > Recovery for up to 10 days.
      `;
      alert(successMessage);
      
      // If this was the selected client, clear selection
      if (selectedClient?.id === clientId) {
        setSelectedClient(null);
        navigate('/');
      }
      
      await fetchClients();
      await fetchContacts();
      await fetchDashboardStats();
      
    } catch (error) {
      console.error('Failed to delete client:', error);
      
      if (error.response?.status === 401) {
        alert('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        alert('ΓÜá∩╕Å Access Denied: Only administrators can delete clients.\n\nPlease contact your team administrator if you need to delete this client.');
      } else if (error.response?.status === 404) {
        alert('Client not found.');
      } else {
        const errorMsg = error.response?.data?.detail || error.message || 'Unknown error occurred';
        alert(`Failed to delete client: ${errorMsg}`);
      }
    }
  };

  // Project management
  const handleProjectSave = async (projectData) => {
    try {
      if (editingProject) {
        // Update existing project
        await api.put(`/projects/${editingProject.id}`, projectData);
      } else {
        // Create new project - check if AI processing is requested
        if (projectData.process_with_ai && projectData.scope_of_work.trim()) {
          // Use the enhanced AI endpoint
          const response = await api.post('/projects/create-with-ai', projectData, {
            params: selectedClient ? { client_id: selectedClient.id } : {}
          });
          
          // Show AI processing result to user with detailed feedback
          if (response.data.ai_processing) {
            const aiResult = response.data.ai_processing;
            
            if (aiResult.status === 'success') {
              // Show detailed success notification
              alert(`≡ƒÄë Project Created Successfully!\n\n≡ƒñû AI Processing Results:\nΓ£à Generated ${aiResult.task_count} tasks from your scope of work\n≡ƒôï Tasks have been automatically organized with priorities and assignments\n\n≡ƒöì Check the project's task view to see all generated tasks!`);
            } else if (aiResult.status === 'error') {
              alert(`ΓÜá∩╕Å Project created, but AI processing encountered an error:\n${aiResult.message}\n\nYou can manually create tasks for this project.`);
            } else if (aiResult.status === 'no_tasks') {
              alert(`Γä╣∩╕Å Project created successfully!\n\n≡ƒñû AI Analysis: No specific tasks could be identified from the scope of work provided.\n\nTry using more detailed, action-oriented language in your scope for better task generation.`);
            } else {
              alert(`Γä╣∩╕Å Project created successfully!\n\n≡ƒñû AI Status: ${aiResult.message}`);
            }
          }
        } else {
          // Use the regular project creation endpoint
          await api.post('/projects', {
            ...projectData,
            ...(selectedClient ? { client_id: selectedClient.id } : {})
          });
        }
      }
      
      setShowProjectForm(false);
      setEditingProject(null);
      
      // Refresh data
      if (selectedClient) {
        fetchProjects(selectedClient.id);
      } else {
        // If no client selected (company project), trigger organization dashboard refresh
        setOrganizationRefreshTrigger(prev => prev + 1);
      }
      fetchDashboardStats();
      
    } catch (error) {
      console.error('Failed to save project:', error);
      alert(`Γ¥î Error creating project: ${error.response?.data?.detail || error.message}\n\nPlease try again or contact support if the issue persists.`);
    }
  };

  const handleTaskClick = async (task, options = {}) => {
    const { skipNavigation = false, captureReturnPath = true, updateUrl = !skipNavigation } = options;
    try {
      console.log('Opening task:', task, 'skipNavigation:', skipNavigation, 'captureReturnPath:', captureReturnPath, 'updateUrl:', updateUrl);
      
      // Store the current path so we can return to it after closing the task modal
      // Store in BOTH state and sessionStorage for reliability
      if (captureReturnPath) {
        const currentPath = location.pathname.startsWith('/tasks/') ? '/tasks' : location.pathname;
        setTaskModalReturnPath(currentPath);
        sessionStorage.setItem('taskModalReturnPath', currentPath);
        console.log('≡ƒôì Stored return path:', currentPath);
      }
      setTaskModalAllowUrlSync(updateUrl);
      
      // Always fetch the full task data to ensure we have correct project_stages
      // This ensures stages are correct regardless of where the task was clicked from
      let fullTask = task;
      if (task.id) {
        try {
          console.log('Fetching full task data for:', task.id);
          const taskResponse = await api.get(`/tasks/${task.id}`);
          fullTask = taskResponse.data;
          console.log('Fetched full task with stages:', fullTask);
        } catch (fetchError) {
          console.warn('Could not fetch full task, using provided data:', fetchError);
          // Fall back to provided task data if fetch fails
        }
      }
      
      // Find the project for this task (for additional context if needed)
      const projectsArray = Array.isArray(projects) ? projects : [];
      let project = projectsArray.find(p => p.id === fullTask.project_id);
      
      // If project not found in state, fetch it
      if (!project && fullTask.project_id) {
        console.log('Project not in state, fetching:', fullTask.project_id);
        try {
          const projectResponse = await api.get(`/projects/${fullTask.project_id}`);
          project = projectResponse.data;
          console.log('Fetched project:', project);
        } catch (projectError) {
          console.warn('Could not fetch project:', projectError);
        }
      }
      
      if (project) {
        setSelectedProjectForTask(project);
      }
      
      setEditingTask(fullTask);
      setShowTaskModal(true);
      
      // Navigate to task URL for deep linking (only if we have a task ID and navigation is not skipped)
      // Skip navigation when opening from project view to keep project open
      if (fullTask.id && updateUrl) {
        navigate(`/tasks/${fullTask.id}`, { replace: false });
      }
    } catch (error) {
      console.error('Failed to open task:', error);
    }
  };

  // Handle task modal from URL - open task when urlTaskId is present
  useEffect(() => {
    const openTaskFromUrl = async () => {
      if (!urlTaskId || !user) return;
      
      // Don't re-open if we already have this task open
      if (editingTask?.id === urlTaskId && showTaskModal) return;
      
      console.log('≡ƒöù Opening task from URL:', urlTaskId);
      
      try {
        const taskResponse = await api.get(`/tasks/${urlTaskId}`);
        
        const fullTask = taskResponse.data;
        
        // Fetch project if needed
        if (fullTask.project_id) {
          try {
            const projectResponse = await api.get(`/projects/${fullTask.project_id}`);
            setSelectedProjectForTask(projectResponse.data);
          } catch (projectError) {
            console.warn('Could not fetch project:', projectError);
          }
        }
        
        setEditingTask(fullTask);
        setShowTaskModal(true);
      } catch (error) {
        console.error('Failed to open task from URL:', error);
        // If task doesn't exist, navigate back to tasks
        navigate('/tasks', { replace: true });
      }
    };
    
    openTaskFromUrl();
  }, [urlTaskId, user]);

  // Listen for openTaskModal events (for recurring templates and other components)
  useEffect(() => {
    const handleOpenTaskModal = async (event) => {
      const task = event.detail;
      console.log('≡ƒô¥ Event: Open task modal', task);
      // Skip navigation when opening via event (from project view)
      // This keeps the project view open while showing the task modal
      await handleTaskClick(task, { skipNavigation: true, captureReturnPath: true, updateUrl: false });
    };
    
    // Listen for createNewTask events - opens modal in create mode with template support
    const handleCreateNewTask = async (event) => {
      const { projectId, stage } = event.detail || {};
      console.log('≡ƒô¥ Event: Create new task', { projectId, stage });
      // Ensure close returns to the current page when creating from embedded contexts.
      const currentPath = location.pathname.startsWith('/tasks/') ? '/tasks' : location.pathname;
      setTaskModalReturnPath(currentPath);
      sessionStorage.setItem('taskModalReturnPath', currentPath);
      setTaskModalAllowUrlSync(false);
      
      // Set up for new task creation (no editingTask = shows template button)
      setEditingTask(null);
      
      // Set the project context
      if (projectId) {
        try {
          const projectResponse = await api.get(`/projects/${projectId}`);
          setSelectedProjectForTask(projectResponse.data);
        } catch (error) {
          console.error('Failed to load project:', error);
        }
      }
      
      setShowTaskModal(true);
    };
    
    window.addEventListener('openTaskModal', handleOpenTaskModal);
    window.addEventListener('createNewTask', handleCreateNewTask);
    return () => {
      window.removeEventListener('openTaskModal', handleOpenTaskModal);
      window.removeEventListener('createNewTask', handleCreateNewTask);
    };
  }, [projects, location.pathname]);

  // Quick task management
  const handleQuickAddTask = async (taskData) => {
    try {
      console.log('≡ƒÜÇ Creating task with data:', taskData);
      
      console.log('≡ƒöæ Token exists:', !!localStorage.getItem('access_token'));
      
      const projectId = taskData.project_id;
      console.log('≡ƒôü Project ID:', projectId);
      
      if (!projectId) {
        console.error('Γ¥î No project selected');
        alert('Please select a project for this task.');
        return;
      }
      
      if (!taskData.name || !taskData.name.trim()) {
        console.error('Γ¥î Task name is empty');
        alert('Please enter a task name.');
        return;
      }
      
      // Clean task data for API
      const cleanTaskData = { ...taskData };
      delete cleanTaskData.client_id; // Remove client_id since it's not needed in project endpoint
      delete cleanTaskData.project_id; // Remove project_id since it's in the URL
      
      console.log('≡ƒº╣ Clean task data:', cleanTaskData);
      console.log('≡ƒîÉ API URL:', `/projects/${projectId}/tasks`);
      
      const response = await api.post(`/projects/${projectId}/tasks`, cleanTaskData);
      
      console.log('Γ£à Task created successfully:', response.data);
      alert('Task created successfully!');
      
      // Refresh dashboard stats to show updated task count
      fetchDashboardStats();
    } catch (error) {
      console.error('Γ¥î Failed to add quick task:', error);
      console.error('≡ƒôè Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 400) {
        alert(`Task creation failed: ${error.response?.data?.detail || 'Invalid data provided'}`);
      } else if (error.response?.status === 401) {
        alert('Please log in again to create tasks.');
      } else if (error.response?.status === 403) {
        alert('You do not have permission to create tasks in this project.');
      } else if (error.response?.status === 404) {
        alert('Project not found. Please select a valid project.');
      } else {
        alert(`Failed to create task: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // Handle company project click
  const handleCompanyProjectClick = async (project) => {
    // For company projects, we don't need a client context
    console.log('≡ƒÅó Opening company project:', project.name, 'ID:', project.id);
    
    // If project has a fake ID (like "general"), fetch the real project
    if (project.id === 'general' || typeof project.id === 'string' && project.id.length < 30) {
      try {
        const response = await api.get('/projects');
        
        // Defensive check - ensure response.data is an array
        const projectsData = Array.isArray(response.data) ? response.data : 
                            (response.data?.projects && Array.isArray(response.data.projects)) ? response.data.projects : [];
        
        // Find the matching project by name without client
        const realProject = projectsData.find(p => !p.client_id && p.name === project.name);
        
        if (realProject) {
          console.log('Γ£à Found real project with UUID:', realProject.id);
          setSelectedClient(null);
          setSelectedProject(realProject);
          navigate(`/projects/${realProject.id}`);
          return;
        }
      } catch (error) {
        console.error('Failed to fetch real project:', error);
      }
    }
    
    // Normal flow - project already has valid UUID
    setSelectedClient(null);
    setSelectedProject(project);
    navigate(`/projects/${project.id}`);
  };

  // Handle recent project click
  const handleRecentProjectClick = (project) => {
    if (project.client_id) {
      // Find the client for this project
      const clientsArray = Array.isArray(clients) ? clients : [];
      const client = clientsArray.find(c => c.id === project.client_id);
      if (client) {
        setSelectedClient(client);
        setSelectedProject(project);
        navigate(`/clients/${client.id}/projects/${project.id}`);
      } else {
        // If client not found, just navigate to project view without client context
        setSelectedClient(null);
        setSelectedProject(project);
        navigate(`/projects/${project.id}`);
      }
    } else {
      // General project without specific client
      setSelectedClient(null);
      setSelectedProject(project);
      navigate(`/projects/${project.id}`);
    }
  };

  // Fetch General project for the team (created automatically on team/client creation)
  const fetchGeneralProject = async () => {
    try {
      // Look for existing General project without client in already loaded projects
      const projectsArray = Array.isArray(projects) ? projects : [];
      const existingGeneral = projectsArray.find(p => !p.client_id && p.name === 'General');
      
      if (existingGeneral) {
        setGeneralProject(existingGeneral);
        return;
      }
      
      // Fetch all projects to find the General project
      const response = await api.get('/projects');
      
      const fetchedProjects = Array.isArray(response.data) ? response.data : 
                              (response.data?.projects && Array.isArray(response.data.projects)) ? response.data.projects : [];
      
      const generalProj = fetchedProjects.find(p => !p.client_id && p.name === 'General');
      
      if (generalProj) {
        setGeneralProject(generalProj);
      }
    } catch (error) {
      console.error('Failed to fetch General project:', error);
    }
  };

  // Handle organization view changes
  const handleOrganizationView = (view) => {
    setOrganizationView(view);
    
    // Navigate to the appropriate URL
    if (view === 'projects') {
      navigate('/organization/projects');
      fetchProjects(); // No clientId = fetch all projects
    } else if (view === 'companies') {
      navigate('/organization/companies');
      fetchClients();
      fetchContacts();
      fetchCrmTasks();
      fetchProjects();
    } else if (view === 'contacts') {
      navigate('/organization/contacts');
      fetchClients();
      fetchContacts();
      fetchCrmTasks();
      fetchProjects();
    } else if (view === 'personal') {
      navigate('/organization/personal');
    } else if (view === 'messaging') {
      navigate('/organization/messaging');
    } else {
      navigate('/organization');
    }
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Show login if no user or user is invalid (missing required fields like id or email)
  if (!user || (!user.id && !user.email)) {
    return (
      <Auth 
        onLogin={login}
        onRegister={register}
        onGoogleLogin={loginWithGoogle}
        onMFAComplete={completeMFALogin}
      />
    );
  }

  const isMessagingView = currentView === 'organization' && organizationView === 'messaging';

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {/* Grace Period Warning Banner */}
      {subscriptionAccess.grace_period && subscriptionAccess.show_warning && showGracePeriodBanner && (
        <GracePeriodBanner
          daysRemaining={subscriptionAccess.days_remaining}
          onDismiss={() => setShowGracePeriodBanner(false)}
          onUpdatePayment={() => {
            navigate('/settings?group=billing&section=subscription');
          }}
        />
      )}
      
      <div className="flex flex-1 bg-gray-50 min-h-0">
        <Sidebar
          clients={clients}
          contacts={contacts}
          onSelectClient={handleSelectClient}
          onSelectContact={handleSelectContact}
          selectedClient={selectedClient}
          onNewClient={() => {
            setEditingClient(null);
            setShowClientForm(true);
          }}
          onDeleteClient={handleClientDelete}
          onDashboard={() => {
            setSelectedClient(null);
            setSelectedProject(null);
            navigate('/');
          }}
          onSchedule={() => navigate('/schedule')}
          onInbox={() => navigate('/inbox')}
          onTaskHub={() => {
            setSelectedClient(null);
            navigate('/tasks');
          }}
          onSettings={() => navigate('/settings')}
          onGovernance={() => navigate('/governance')}
          governanceAccess={governanceAccess}
          user={user}
          onLogout={logout}
          currentView={currentView}
          organizationName={organizationName}
          onOrganizationView={handleOrganizationView}
          organizationView={organizationView}
          notificationCount={notificationCount}
          onNotifications={() => setShowNotifications(true)}
          onUserProfile={() => setShowUserProfile(true)}
          currentOrganization={currentOrganization}
          onOrganizationChange={handleOrganizationChange}
          badgeCounts={badgeCounts}
          isMessagingView={isMessagingView}
        />
        <div className="flex-1 min-w-0 flex flex-col">
        
        {/* Notification Center */}
        <NotificationCenter
          isOpen={showNotifications}
          onClose={() => {
            setShowNotifications(false);
            fetchNotificationCount(); // Refresh count after closing
          }}
          onNotificationsChanged={() => {
            fetchNotificationCount();
            fetchBadgeCounts();
          }}
        user={user}
      />
      
      {currentView === 'dashboard' ? (
        <Dashboard stats={dashboardStats} onQuickAddTask={handleQuickAddTask} clients={clients} onRecentProjectClick={handleRecentProjectClick} teamMembers={teamMembers} onTaskClick={handleTaskClick} user={user} upcomingMeetings={upcomingMeetings} />
      ) : currentView === 'schedule' ? (
        <ScheduleDashboard user={user} />
      ) : currentView === 'inbox' ? (
        <UnifiedInbox />
      ) : currentView === 'task-hub' ? (
        <TaskHub
          teamMembers={teamMembers}
          onTaskUpdate={handleTaskClick}
          onAddTask={() => {
            setQuickTaskPrefilledData({});
            setShowQuickTaskModal(true);
          }}
          onClearClientFilter={() => {
            setSelectedClient(null);
            navigate('/tasks');
          }}
          refreshTrigger={taskHubRefreshTrigger}
          clientFilter={selectedClient}
        />
      ) : currentView === 'client-tasks' && selectedClient ? (
        <TaskHub
          title="Client Tasks"
          teamMembers={teamMembers}
          onTaskUpdate={handleTaskClick}
          onAddTask={() => {
            setQuickTaskPrefilledData(selectedClient ? { client_id: selectedClient.id } : {});
            setShowQuickTaskModal(true);
          }}
          onClearClientFilter={() => {
            navigate(`/clients/${selectedClient.id}`);
          }}
          refreshTrigger={taskHubRefreshTrigger}
          clientFilter={selectedClient}
        />
      ) : currentView === 'organization' ? (
        (organizationView === 'companies' || organizationView === 'contacts') ? (
          <CompaniesCRMView
            clients={clients}
            contacts={contacts}
            crmTasks={crmTasks}
            initialTab={organizationView === 'contacts' ? 'contacts' : 'companies'}
            onViewProjects={(company) => handleSelectClient(company)}
            onViewTasks={(company) => {
              setSelectedClient(company);
              navigate(`/clients/${company.id}/tasks`);
            }}
            onOpenSettings={(company) => {
              setSelectedClient(company);
              navigate(`/clients/${company.id}`);
            }}
            onContactDeleted={async (contactId) => {
              setContacts(prev => (prev || []).filter(c => c.id !== contactId));
              await fetchContacts();
            }}
            onCompanyCreated={async () => {
              await fetchClients();
            }}
            onContactCreated={async () => {
              await fetchContacts();
            }}
          />
        ) : (
          <OrganizationDashboard 
            organizationName={organizationName}
            view={organizationView}
            user={user}
            onProjectClick={handleCompanyProjectClick}
            onNewProject={() => {
              setSelectedClient(null);
              setEditingProject(null);
              setShowProjectForm(true);
            }}
            refreshTrigger={organizationRefreshTrigger}
          />
        )
      ) : currentView === 'settings' ? (
        <Settings 
          user={user} 
          currentOrganization={currentOrganization}
          onBack={handleBackToDashboard}
          onShowImportWizard={handleShowImportWizard}
        />
      ) : currentView === 'governance' ? (
        <Governance key={currentOrganization?.id || 'no-org'} user={user} currentOrganization={currentOrganization} />
      ) : currentView === 'project' && selectedProject ? (
        <ProjectDetail
          project={selectedProject}
          client={selectedClient}
          teamMembers={teamMembers}
          user={user}
          onBack={() => {
            setSelectedProject(null);
            if (selectedClient) {
              navigate(`/clients/${selectedClient.id}`);
            } else {
              navigate('/organization/projects');
            }
          }}
        />
      ) : selectedClient ? (
        <ClientDetail
          client={selectedClient}
          projects={projects}
          user={user}
          currentOrganization={currentOrganization}
          onNewProject={() => {
            setEditingProject(null);
            setShowProjectForm(true);
          }}
          onSelectProject={(project) => {
            setSelectedProject(project);
            navigate(`/clients/${selectedClient.id}/projects/${project.id}`);
          }}
          onEditClient={(client) => {
            setEditingClient(client);
            setShowClientForm(true);
          }}
          onOpenTask={handleTaskClick}
          onClientUpdate={(updatedClient) => {
            // Update the client in the clients list
            setClients(prev => prev.map(c => c.id === updatedClient.id ? updatedClient : c));
            // Update selected client if it matches
            if (selectedClient?.id === updatedClient.id) {
              setSelectedClient(updatedClient);
            }
          }}
          badgeCounts={badgeCounts}
          markProjectViewed={markProjectViewed}
        />
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">Select a client</h2>
            <p className="text-gray-500">Choose a client from the sidebar to view details</p>
          </div>
        </div>
      )}

      {showClientForm && (
        <EnhancedClientForm
          client={editingClient}
          onSave={handleClientSave}
          onImport={handleClientImport}
          onCancel={() => {
            setShowClientForm(false);
            setEditingClient(null);
          }}
          onDelete={(clientId) => {
            handleClientDelete(clientId, editingClient?.name);
            setShowClientForm(false);
            setEditingClient(null);
          }}
          userSettings={userSettings}
        />
      )}

      {showProjectForm && (
        <>
          {console.log('≡ƒƒó Rendering ProjectForm modal, showProjectForm:', showProjectForm)}
          <ProjectForm
            project={editingProject}
            onSave={handleProjectSave}
            onCancel={() => {
              setShowProjectForm(false);
              setEditingProject(null);
            }}
          />
        </>
      )}

      {showTaskModal && (
        <TaskModal
          isOpen={showTaskModal}
          onClose={() => {
            // Read return path directly from sessionStorage to avoid stale closure issues
            // This handles navigation from Schedule, Project views, etc.
            const returnPath = sessionStorage.getItem('taskModalReturnPath') || taskModalReturnPath;
            setShowTaskModal(false);
            setEditingTask(null);
            setSelectedProjectForTask(null);
            setTaskModalAllowUrlSync(true);
            // Navigate back to where we came from if we stored a return path
            // Otherwise fallback to checking URL pattern
            if (returnPath) {
              sessionStorage.removeItem('taskModalReturnPath');
              navigate(returnPath);
              setTaskModalReturnPath(null);
            } else if (taskModalAllowUrlSync && location.pathname.startsWith('/tasks/') && location.pathname !== '/tasks') {
              navigate('/tasks'); // Go back to Task Hub
            }
          }}
          task={editingTask}
          onSave={async (taskData) => {
            try {
              const token = localStorage.getItem('access_token');
              
              // Determine which project to use
              let projectId = selectedProjectForTask?.id;
              
              // If no project selected or project ID is "general", use the General project
              if (!projectId || projectId === 'general') {
                if (!generalProject) {
                  await fetchGeneralProject();
                }
                projectId = generalProject?.id;
              }
              
              if (!projectId && !editingTask?.id) {
                console.error('Γ¥î No project available for new task');
                alert('No project available. Please try again.');
                return;
              }
              
              const endpoint = editingTask?.id 
                ? `/tasks/${editingTask.id}` 
                : `/projects/${projectId}/tasks`;
              
              const method = editingTask?.id ? 'PUT' : 'POST';
              console.log(`≡ƒîÉ ${method} ${endpoint}`);
              
              const response = await api[method.toLowerCase()](endpoint, taskData);

              console.log('Γ£à Task saved successfully:', response.data);
              console.log('≡ƒöì _autoSave flag:', taskData._autoSave);
              console.log('≡ƒöì _keyboardShortcut flag:', taskData._keyboardShortcut);
              
              // Only show alert for manual saves (when not auto-saving or using keyboard shortcut)
              // Auto-save will be handled by TaskManagement component
              // Keyboard shortcuts should silently save without alerts
              if (!taskData._autoSave && !taskData._keyboardShortcut) {
                console.log('≡ƒôó Showing success alert (manual save)');
                alert(editingTask?.id ? 'Task updated successfully!' : 'Task created successfully!');
              }

              // Only close modal if not auto-saving
              if (!taskData._autoSave) {
                console.log('≡ƒÜ¬ Closing modal (manual save)');
                // Read return path from sessionStorage (same pattern as onClose)
                const returnPath = sessionStorage.getItem('taskModalReturnPath') || taskModalReturnPath;
                setShowTaskModal(false);
                setEditingTask(null);
                setSelectedProjectForTask(null);
                setTaskModalAllowUrlSync(true);
                // Navigate back to where we came from if we stored a return path
                // Otherwise only navigate if we're on a task-specific URL (e.g., /tasks/123)
                // If in a project view or other context, just close the modal and stay put
                if (returnPath) {
                  sessionStorage.removeItem('taskModalReturnPath');
                  navigate(returnPath);
                  setTaskModalReturnPath(null);
                } else if (taskModalAllowUrlSync && location.pathname.startsWith('/tasks/') && location.pathname !== '/tasks') {
                  // Only navigate to Task Hub if we opened task from a direct task URL
                  navigate('/tasks');
                }
                // Otherwise: stay on current page (e.g., project view, dashboard, etc.)
              } else {
                console.log('Γ£ï Keeping modal open (auto-save)');
                // For auto-save, update the editing task with the new ID and URL
                if (!editingTask?.id && response.data?.task?.id) {
                  setEditingTask({...taskData, id: response.data.task.id});
                  console.log('≡ƒåö Updated editing task with new ID:', response.data.task.id);
                  // Update URL to include the new task ID
                  if (taskModalAllowUrlSync) {
                    navigate(`/tasks/${response.data.task.id}`, { replace: true });
                  }
                }
              }
              
              // Refresh dashboard if we're on dashboard view
              if (currentView === 'dashboard') {
                fetchDashboardStats();
              }
              
              // Refresh task hub if we're on task hub view
              if (currentView === 'task-hub') {
                setTaskHubRefreshTrigger(prev => prev + 1);
              }
              
              // Dispatch event to refresh any open project Kanban boards
              window.dispatchEvent(new CustomEvent('taskUpdated', { 
                detail: { 
                  task: response.data?.task || response.data,
                  projectId: projectId || editingTask?.project_id
                } 
              }));
              
              // Return the saved task data for pending operations (attachments, etc.)
              return response.data?.task || response.data;
            } catch (error) {
              console.error('Γ¥î Failed to save task:', error);
              console.error('≡ƒôè Error details:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                message: error.message
              });
              
              if (error.response?.status === 400) {
                alert(`Task save failed: ${error.response?.data?.detail || 'Invalid data provided'}`);
              } else if (error.response?.status === 401) {
                alert('Please log in again to save tasks.');
              } else if (error.response?.status === 403) {
                alert('You do not have permission to save tasks in this project.');
              } else if (error.response?.status === 404) {
                alert('Project or task not found.');
              } else {
                alert(`Failed to save task: ${error.response?.data?.detail || error.message}`);
              }
              
              throw error;
            }
          }}
          project={selectedProjectForTask}
          teamMembers={teamMembers}
          stages={editingTask?.project_stages || selectedProjectForTask?.stages || []}
          user={user}
          openAttachmentPreview={openAttachmentPreview}
          onEditSubtask={async (subtask) => {
            // Directly swap to subtask without closing the modal
            // This provides a seamless transition
            console.log('≡ƒô¥ Swapping to subtask:', subtask);
            
            // Fetch full subtask data
            try {
              const taskResponse = await api.get(`/tasks/${subtask.id}`);
              const fullSubtask = taskResponse.data;
              
              // Find project for subtask
              const projectsArray = Array.isArray(projects) ? projects : [];
              let project = projectsArray.find(p => p.id === fullSubtask.project_id);
              
              if (!project && fullSubtask.project_id) {
                try {
                  const projectResponse = await api.get(`/projects/${fullSubtask.project_id}`);
                  project = projectResponse.data;
                } catch (e) {
                  console.warn('Could not fetch project for subtask');
                }
              }
              
              if (project) {
                setSelectedProjectForTask(project);
              }
              
              // Directly update the editing task - modal stays open
              setEditingTask(fullSubtask);
            } catch (error) {
              console.error('Failed to load subtask:', error);
              alert('Failed to open subtask');
            }
          }}
          onDelete={async (taskId) => {
            try {
              await api.delete(`/tasks/${taskId}`);
              
              // Read return path for navigation
              const returnPath = sessionStorage.getItem('taskModalReturnPath') || taskModalReturnPath;
              
              // Close modal and clear state
              setShowTaskModal(false);
              setEditingTask(null);
              setSelectedProjectForTask(null);
              setTaskModalAllowUrlSync(true);
              
              // Navigate back to where user came from
              if (returnPath) {
                sessionStorage.removeItem('taskModalReturnPath');
                navigate(returnPath);
                setTaskModalReturnPath(null);
              } else if (taskModalAllowUrlSync && location.pathname.startsWith('/tasks/') && location.pathname !== '/tasks') {
                navigate('/tasks');
              }
              
              // Refresh views
              if (currentView === 'dashboard') {
                fetchDashboardStats();
              }
              if (currentView === 'task-hub') {
                setTaskHubRefreshTrigger(prev => prev + 1);
              }
              
              // Dispatch event to refresh any open project views
              window.dispatchEvent(new CustomEvent('taskDeleted', { detail: { taskId } }));
              
            } catch (error) {
              console.error('Failed to delete task:', error);
              alert(`Failed to delete task: ${error.response?.data?.detail || error.message}`);
            }
          }}
          onArchive={async (taskId, currentlyArchived) => {
            try {
              await api.put(`/tasks/${taskId}/archive`, { archived: !currentlyArchived });
              
              // Refresh the editing task to reflect new state
              const response = await api.get(`/tasks/${taskId}`);
              setEditingTask(response.data);
              
              // Refresh views
              if (currentView === 'dashboard') {
                fetchDashboardStats();
              }
              if (currentView === 'task-hub') {
                setTaskHubRefreshTrigger(prev => prev + 1);
              }
              
              // Dispatch event to refresh any open project views
              window.dispatchEvent(new CustomEvent('taskUpdated', { 
                detail: { task: response.data, projectId: response.data.project_id } 
              }));
              
            } catch (error) {
              console.error('Failed to archive task:', error);
              alert(`Failed to ${currentlyArchived ? 'unarchive' : 'archive'} task: ${error.response?.data?.detail || error.message}`);
            }
          }}
        />
      )}

      {showUserProfile && (
        <UserProfile
          user={user}
          onClose={() => setShowUserProfile(false)}
          onUserUpdate={(updatedUser) => {
            updateUser(updatedUser);
            console.log('User profile updated:', updatedUser);
          }}
        />
      )}

      {/* Import Wizard */}
      <ImportWizard
        isOpen={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onComplete={handleImportWizardComplete}
      />

      {/* Attachment Preview Modal */}
      <AttachmentPreview
        attachment={previewAttachment}
        isOpen={showPreview}
        onClose={closeAttachmentPreview}
      />
      
      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsHelp 
        isOpen={showKeyboardHelp} 
        onClose={() => setShowKeyboardHelp(false)} 
      />
      
      {/* Quick Add Task Modal (for keyboard shortcut) */}
      <QuickAddTaskModal
        isOpen={showQuickTaskModal}
        onClose={() => {
          setShowQuickTaskModal(false);
          setQuickTaskPrefilledData({});
        }}
        onTaskCreated={(newTask) => {
          console.log('Task created via keyboard shortcut:', newTask);
          // Trigger refresh
          window.dispatchEvent(new CustomEvent('taskUpdated', { 
            detail: { task: newTask, projectId: newTask?.project_id } 
          }));
        }}
        onOpenFullEditor={(taskData) => {
          setShowQuickTaskModal(false);
          // Open the full task modal with pre-filled data
          if (taskData) {
            handleTaskClick(taskData, { skipNavigation: true });
          }
        }}
        user={user}
        prefilledData={quickTaskPrefilledData}
      />

      </div>
      </div>
    </div>
  );
}

// Simple route checking component
const AppRouter = () => {
  // Check URL path on page load and in real-time
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  
  useEffect(() => {
    // Listen for URL changes
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  // Show Super Admin if URL starts with /super-admin
  if (currentPath.startsWith('/super-admin')) {
    return <SuperAdminApp />;
  }
  
  // Show main Athena app for all other URLs
  return <App />;
};

// Wrap App with ThemeProvider and simple routing
import { Toaster } from 'sonner';

const AppWithTheme = () => {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <ThemeProvider>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default AppWithTheme;
// force vercel rebuild
